<template>
  <div class="viewer-container">
    <div ref='viewer' class="viewer-element"></div>
    
    <!-- Navigation Overlay -->
    <div v-if="(signatureMode || simulationMode) && guidedSigningReady" class="nav-overlay">
      <div class="nav-button-container">
        <button 
          class="nav-button" 
          :class="{ 'finish-button': nextButtonText === 'Finish' }"
          @click="handleNavButtonClick"
        >
          {{ nextButtonText }}
        </button>
      </div>
    </div>

    <!-- Arrow Overlay -->
    <div 
      v-if="showArrow && (signatureMode || simulationMode)" 
      class="arrow-indicator"
      :style="{ top: arrowPosition.top + 'px', left: arrowPosition.left + 'px' }"
    >
      <div class="arrow-content">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="#ffc107">
          <path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z" transform="rotate(-90 12 12)"/>
        </svg>
        <div class="field-label">{{ currentFieldLabel }}</div>
      </div>
    </div>

    <popup ref="pdfSaveDialog"
           title="Saving PDF Document"
           okText="Okay"
           okColor="green"
           :isPersistent=true
           :showClose=false
           @savePromise="pdfSaveComplete">
      <template v-slot:default="slotProps">
        <h3>Action Completed</h3>
        {{ pdfResponse }}
      </template>
    </popup>
  </div>
</template>

<script>
/* eslint-disable */
import WebViewer from '@pdftron/webviewer';
import {VueController} from '@/service/support/vuex-controller'
import vaultService from "@/service/vault-service";

export default VueController('WebViewer', {
  props: {
    docUrl: String,
    xfdfOverride: String,
    file: Object,
    vault: Object,
    signatureMode: {
      type: Boolean,
      default: false
    },
    simulationMode: {
      type: Boolean,
      default: false
    },
    signatureRequestId: {
      type: String,
      default: null
    },
    requiredFieldsXfdf: {
      type: String,
      default: null
    },
    fieldAssignments: {
      type: Object,
      default: null
    },
    recipientEmail: {
      type: String,
      default: null
    }
  },
  watch: {
    docUrl: function () {
      if (this.instance) {
        this.resetViewerState();
        this.instance.UI.loadDocument(this.docUrl, {extension: this.file.extension.toLowerCase()});
      }
    },
    fieldAssignments: {
        handler(newVal) {
            console.log('🔄 fieldAssignments prop updated in WebViewer:', newVal ? Object.keys(newVal).length + ' assignments' : 'null');
            // If assignments update after load (race condition), re-apply them to annotations
            if (this.instance && this.guidedSigningReady) {
                this.applyFieldAssignments();
            }
        },
        deep: true
    }
  },
  data() {
    return {
      instance: null,
      pdfResponse: "",
        pdfFormDirty: false,
        signatureChanged: false,
        requiredFields: [],
        currentFieldIndex: -1, // Start at -1 for "Start" button state
        allFieldsCompleted: false,
        guidedSigningReady: false,
        nextButtonText: "Start",
      showArrow: false,
      arrowPosition: { top: 0, left: 0 },
      currentFieldLabel: "",
      activeParticipantEmail: null,
      isScrolling: false,
      isProgrammaticUpdate: false,
      fieldStylingTimeout: null, // For debouncing applyFieldStyling calls
    }
  },
  state: [
    "organization",
    "user",
    "cognito"
  ],
  computed: {
    isGuest() {
      // First check if we have cognito data
      if (!this.cognito || !this.cognito.attributes) {
        return true;
      }

      const userId = this.cognito.attributes.sub;

      // Check vault-specific permissions first
      if (this.vault && this.vault.permissions) {
        const permission = this.vault.permissions.find(p => p.user_id === userId && p._deleted !== 1);
        if (permission) {
          // If user has a vault permission entry, check their user_type for this vault
          return permission.user_type === 'guest';
        }
      }

      // Check organization permissions
      if (this.organization && this.organization.permissions) {
        const orgPermission = this.organization.permissions.find(p => p.user_id === userId && p._deleted !== 1);
        if (orgPermission) {
          // Organization members are not guests
          return false;
        }
      }

      // Fall back to global user type
      const userType = this.cognito.attributes["custom:mutableUserType"];
      return userType !== "member" && userType !== "admin";
    }
  },
  beforeDestroy() {
    // Cleanup timers
    if (this.fieldStylingTimeout) {
      clearTimeout(this.fieldStylingTimeout);
      this.fieldStylingTimeout = null;
    }
    
    // Cleanup listeners
    if (this.instance && this.instance.Core) {
      const documentViewer = this.instance.Core.documentViewer;
      if (documentViewer) {
        documentViewer.removeEventListener('scroll', this.onViewerScroll);
        documentViewer.removeEventListener('zoomUpdated', this.onViewerScroll);
      }
      
      if (this.instance.UI) {
        this.instance.UI.removeEventListener('keyDown', this.handleViewerKeyDown);
      }
    }
    window.removeEventListener('keydown', this.handleWindowKeyDown);
    window.removeEventListener('resize', this.onViewerScroll);
  },
  mounted: function () {
    console.log("🚀 WebViewer Component Mounted - Debug Version 8");
    let self = this;

    WebViewer.Iframe({
      path: '/webviewer/lib',
      licenseKey: 'Breichbilt LLC.:OEM:a5jOK0000010xKT::B+3:AMS(20260929):6376D0F4337DD7E43171D05109D698D288C7D6C3D908272E5B2A1E9A2B57F2E97D656D',
      initialDoc: self.docUrl && self.docUrl !== '' ? self.docUrl : undefined,
      extension: self.file.extension.toLowerCase(),
      fullAPI: true,  // Enable full PDF engine for digital signatures and advanced features
      disabledElements: [], // Keep all UI elements enabled
      // Configure PDF rendering to regenerate widget appearances instead of using potentially corrupted embedded ones
      config: {
        forceClientSideInit: true  // Force client-side rendering to avoid appearance stream issues
      }
    }, self.$refs.viewer).then(async (instance) => {
      self.instance = instance;

      instance.Core.documentViewer.addEventListener('documentLoaded', async () => {
        self.$emit("updatePdfDirtyFlag", false);
        //self.configurePdfViewerSettings(instance);
        console.log("Loading PDF Document");
        
        // Inject custom styles to suppress default outlines
        if (self.signatureMode || self.simulationMode) {
          self.injectCustomStyles();
        }
        
        // Validate digital signatures after document loads
        setTimeout(async () => {
          try {
            const signatureInfo = await self.validatePdfSignatures();
            if (signatureInfo) {
              self.$emit("pdfSignatureValidation", signatureInfo);
            }
          } catch (error) {
            console.error("Error validating PDF signatures:", error);
          }
        }, 1000);
      });

      instance.Core.documentViewer.addEventListener('documentUnloaded', function () {
        self.resetViewerState();
      });

      let updateFieldChanged = (field, value) => {
        console.log('🔵 fieldChanged event fired:', field, value);
        self.pdfFormDirty = true;
        self.$emit("updatePdfDirtyFlag", true);
        
        // Update field styling in signature mode (including simulation mode)
        if (self.signatureMode || self.simulationMode) {
          console.log('🔵 Scheduling applyFieldStyling (debounced)...');
          self.scheduleFieldStyling(250);
          if (self.requiredFields.length > 0) {
            self.checkAllFieldsComplete();
          }
        }
      };

      let updateAnnotationChanged = (annotations, action, e) => {
        if (self.isProgrammaticUpdate) return; // Prevent infinite loops from our own styling updates
        
        console.log('🟢 annotationChanged event fired:', annotations, action);
        self.pdfFormDirty = true;
        if (!e.imported) {
          self.$emit("updatePdfDirtyFlag", true);

          if (annotations.length > 0 && annotations[0].Subject === "Signature") {
            self.signatureChanged = true;
            console.log('🟢 Signature annotation detected');
          }
          
          // Update field styling in signature mode (including simulation mode)
          if (self.signatureMode || self.simulationMode) {
            console.log('🟢 Scheduling applyFieldStyling (debounced)...');
            self.scheduleFieldStyling(150);
            if (self.requiredFields.length > 0) {
              self.checkAllFieldsComplete();
            }
          }
        }
      };

      instance.Core.documentViewer.addEventListener('annotationsLoaded', async () => {
        console.log('📄 Annotations loaded, attaching event listeners...');
        
        // CRITICAL FIX: Regenerate appearance streams BEFORE attaching listeners
        // The PDF may contain corrupted appearance streams that cause rendering errors
        try {
          console.log('🔧 Regenerating widget appearances to fix corrupted streams...');
          const annotManager = instance.Core.annotationManager;
          const annotations = annotManager.getAnnotationsList();
          let fixedCount = 0;
          
          annotations.forEach(annot => {
            if (annot.Subject === 'Widget' || annot.isWidget) {
              try {
                // Clear corrupted appearance dictionary
                if (annot.AP) {
                  delete annot.AP;
                  fixedCount++;
                }
              } catch (e) {
                console.warn('Could not clear appearance for:', annot.fieldName, e);
              }
            }
          });
          
          if (fixedCount > 0) {
            console.log(`✅ Cleared ${fixedCount} corrupted widget appearances`);
            // Force a redraw with fresh appearances
            annotManager.drawAnnotations();
          }
        } catch (error) {
          console.error('Error regenerating widget appearances:', error);
        }
        
        instance.Core.annotationManager.addEventListener('fieldChanged', updateFieldChanged);
        instance.Core.annotationManager.addEventListener('annotationChanged', updateAnnotationChanged);
        console.log('📄 Event listeners attached successfully');

        // Set current user if available (may be null for guest signature flows)
        if (self.user && self.user.displayName) {
          instance.Core.annotationManager.setCurrentUser(self.user.displayName);
        } else if (self.cognito && self.cognito.attributes && self.cognito.attributes.name) {
          instance.Core.annotationManager.setCurrentUser(self.cognito.attributes.name);
        } else {
          instance.Core.annotationManager.setCurrentUser('Guest User');
        }

        // Setup guided signing for both modes (uses FieldManager to detect all form fields)
        if (self.signatureMode || self.simulationMode) {
          try {
            self.setupGuidedSigning(instance);
          } catch (error) {
            console.error('Error setting up guided signing:', error);
          }
        }

        //UI Settings
        instance.UI.setLayoutMode(instance.UI.LayoutMode.Continuous);
        instance.UI.setToolbarGroup('View');
        instance.UI.setToolMode('Pan');

        // Disable Edit and Forms options for guests
        if (self.isGuest) {
          instance.UI.disableElements(['toolbarGroup-Edit', 'toolbarGroup-Forms']);
        }
        
        // Disable specific UI elements in signature/simulation mode as requested
        if (self.signatureMode || self.simulationMode) {
            instance.UI.disableElements([
                'downloadButton', 
                'printButton', 
                'saveAsButton', 
                'fullscreenButton', 
                'menuButton',       // Settings/Main Menu
                'selectToolButton', // Select Tool
                'toolButton-select',
                'toolbarGroup-Select',
                'annotationEditToolButton', // The specific button identified in HTML
                'rubberStampToolGroupButton', 
                'stampToolGroupButton', 
                'fileAttachmentToolGroupButton',
                'calloutToolGroupButton',
                'miscToolGroupButton',
                'imageToolGroupButton'
            ]);
            
            // Extra measure: ensure default tool is Pan, not Edit
            instance.UI.setToolMode('Pan');
        }
        
        // Disable signature list panel that appears after signing
        instance.UI.disableElements(['signatureListPanel']);
        
        // Disable Stamps panel in signature mode
        if (self.signatureMode) {
          instance.UI.disableElements(['rubberStampToolGroupButton', 'stampToolGroupButton', 'toolButton-rubberStamp']);
        }
        
        // Also prevent it from opening automatically
        instance.UI.addEventListener('signatureListPanelOpened', () => {
          instance.UI.closeElements(['signatureListPanel']);
        });
        
        // Set default signature ink color to blue in Sign Mode
        if (self.signatureMode) {
          try {
            // Set default annotation styles for signature tools to use blue ink
            const { Tools } = instance.Core;
            if (Tools && Tools.SignatureCreateTool) {
              instance.Core.documentViewer.getTool('AnnotationCreateSignature').setStyles({
                StrokeColor: new instance.Core.Annotations.Color(0, 0, 255) // Blue
              });
            }
          } catch (error) {
            console.log('Could not set signature tool color:', error);
          }
        }
        
        // Start persistent style injection to catch late-loading iframes
        if (self.signatureMode || self.simulationMode) {
          self.startStyleInjectionLoop();
          
          // Apply initial field styling after document is fully rendered
          // Use finishedRendering event instead of arbitrary timeout
          const applyInitialStyling = () => {
            console.log('🎨 finishedRendering event fired - waiting for annotation processing...');
            // Small delay to allow PDFTron to finish processing annotation appearances
            // This prevents "Cannot read properties of undefined (reading 'draw')" errors
            setTimeout(() => {
              console.log('🎨 Annotation processing complete - applying field styling now...');
              self.applyFieldStyling();
            }, 100);
            // Remove listener after first render to avoid repeated calls
            instance.Core.documentViewer.removeEventListener('finishedRendering', applyInitialStyling);
          };
          instance.Core.documentViewer.addEventListener('finishedRendering', applyInitialStyling);
        }
      });
    });
  },
  unmounted() {
    if (this.styleInjectionInterval) {
      clearInterval(this.styleInjectionInterval);
    }
    let self = this;
    self.instance.UI.closeDocument().then(function () {
      self.instance.$destroy();
    })
  },
  methods: {
    startStyleInjectionLoop() {
      // Periodically check and inject styles (stops after 5 seconds)
      let attempts = 0;
      this.styleInjectionInterval = setInterval(() => {
        this.injectCustomStyles();
        attempts++;
        if (attempts > 20) { // Stop after ~10 seconds
          clearInterval(this.styleInjectionInterval);
        }
      }, 500);
    },
    injectCustomStyles() {
      try {
        const injectIntoDoc = (doc) => {
          if (!doc) return;
          try {
            const styleId = 'pdftron-custom-styles';
            if (!doc.getElementById(styleId)) {
              const style = doc.createElement('style');
              style.id = styleId;
              style.textContent = `
                /* Suppress default red outline on required fields */
                .text, .choice, .radioButton, .checkBox, .signature {
                  outline: none !important;
                }
                /* Suppress the blue highlight overlay on all fields */
                div[id^="highlight-"] {
                  background-color: transparent !important;
                  display: none !important; /* Try hiding it completely */
                }
                /* Ensure our custom border color takes precedence */
                input:required, textarea:required, select:required {
                  box-shadow: none !important;
                  outline: none !important;
                }
                /* Force removal of any other outlines */
                div[style*="outline: red"] {
                  outline: none !important;
                }
                /* Hide Stamps tab in signature modal (Sign Mode) */
                ${self.signatureMode ? `
                  button[data-element="savedSignaturePanelButton"],
                  .saved-signature-tab,
                  .signature-modal-tab[title*="Stamp"],
                  .signature-modal-tab[title*="stamp"],
                  div[data-element="savedSignaturesPanel"],
                  div[data-element="savedSignatureTab"] {
                    display: none !important;
                  }
                ` : ''}
              `;
              doc.head.appendChild(style);
              // console.log('Injected custom styles into', doc.title || 'frame');
            }
          } catch (e) {
            // Access denied or other error
          }
        };

        // Recursive function to find all frames
        const findAndInject = (win) => {
          try {
            injectIntoDoc(win.document);
            const frames = win.frames;
            for (let i = 0; i < frames.length; i++) {
              findAndInject(frames[i]);
            }
          } catch (e) {
            // Access denied
          }
        };

        // Start from the viewer iframe
        const viewerElement = this.$refs.viewer;
        const iframes = viewerElement.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          try {
            findAndInject(iframe.contentWindow);
          } catch (e) { }
        });
        
      } catch (error) {
        console.error('Error injecting custom styles:', error);
      }
    },
    resetViewerState() {
      if (!this.instance || !this.instance.Core || !this.instance.Core.annotationManager) {
        return;
      }

      let annotationManager = this.instance.Core.annotationManager;
      let annotations = annotationManager.getAnnotationsList();
      if (annotations && annotations.length > 0) {
        annotationManager.deleteAnnotations(annotations, {force: true});
      }

      this.pdfFormDirty = false;
      this.signatureChanged = false;
      this.$emit("updatePdfDirtyFlag", false);
    },
    pdfSaveComplete() {
      this.pdfFormDirty = false;
      //this.$emit("savePdfToParent");
    },
    async savePdf() {
      let self = this;
      try {
        let {Core} = self.instance;
        let doc = Core.documentViewer.getDocument();
        let xfdfString = await Core.annotationManager.exportAnnotations();
        let data = await doc.getFileData({xfdfString});
        if (!data) {
          throw new Error("Unable to retrieve PDF data");
        }

        let blob = new Blob([new Uint8Array(data)], {type: "application/pdf"});
        await vaultService.pdfChangeUpload(blob, self.vault, self.vault.org_id, self.file);

        self.notification("success", 'PDF has been saved.');
        self.$emit("savePdfToParent", null, self.file, self.signatureChanged);
        self.pdfFormDirty = false;
        self.signatureChanged = false;
        self.pdfResponse = "PDF has saved successfully!";
      } catch (error) {
        console.error("Failed to save PDF", error);
        self.notification("error", 'Unable to save PDF, please try again.');
        self.pdfResponse = "PDF has failed to save, please try again or contact support.";
        self.openPopup('pdfSaveDialog');
      } finally {
        self.$emit("updatePdfDirtyFlag", self.pdfFormDirty);
      }
    },
    setupGuidedSigning(instance) {
      const annotationManager = instance.Core.annotationManager;
      const fieldManager = annotationManager.getFieldManager();
      const annotations = annotationManager.getAnnotationsList();
      
      // Use FieldManager for both simulation AND sign mode for consistent field detection
      const pdfFields = fieldManager.getFields();
      
      // PDFTron returns fields as an array-like object with numeric keys
      // Extract actual field names from the field objects
      const fieldValues = Object.values(pdfFields);
      const fieldNames = fieldValues.map(field => field.name).filter(Boolean);
      
      console.log('🔍 FieldManager.getFields() keys:', Object.keys(pdfFields));
      console.log('🔍 Extracted field names:', fieldNames);
      console.log('🔍 Annotations list has', annotations.length, 'annotations');
      
      // Debug: Check what field names annotations have
      const annotFieldNames = annotations.map(a => a.fieldName).filter(Boolean);
      console.log('🔍 Annotation fieldNames:', annotFieldNames);
      
      // Build a map of field names to their first widget annotation
      const fieldWidgetMap = {};
      annotations.forEach(annot => {
        if (annot.fieldName && !fieldWidgetMap[annot.fieldName]) {
          fieldWidgetMap[annot.fieldName] = annot;
          console.log('  ✅ Mapped field:', annot.fieldName, 'to annotation', annot);
        }
      });
      
      console.log('🔍 fieldWidgetMap size:', Object.keys(fieldWidgetMap).length);
      console.log('🔍 Attempting to map', fieldNames.length, 'field names to annotations...');
      
      // Create required fields list with one entry per unique field
      this.requiredFields = fieldNames.map(fieldName => {
        const annot = fieldWidgetMap[fieldName] || annotations.find(a => a.fieldName === fieldName);
        if (!annot) {
          console.warn('  ⚠️ No annotation found for field:', fieldName);
        } else {
          console.log('  ✅ Mapped field:', fieldName);
        }
        return annot;
      }).filter(annot => annot !== undefined);
      
      console.log('✅ Setup guided signing with', this.requiredFields.length, 'unique fields from FieldManager');
      
      if (this.requiredFields.length > 0) {
        console.log('✅ Guided signing ready! Enabling UI.');
        this.currentFieldIndex = -1;
        this.guidedSigningReady = true;
        this.updateButtonState();
        
        // Build a field lookup map by name
        const fieldsByName = {};
        fieldValues.forEach(field => {
          if (field.name) {
            fieldsByName[field.name] = field;
          }
        });
        
        // Build detailed field information for the controller
        const fieldsData = this.requiredFields.map(annot => {
          const field = fieldsByName[annot.fieldName];
          const rect = annot.Rect || annot.rect;
          
          return {
            id: annot.Id,
            fieldName: annot.fieldName,
            displayName: annot.fieldName, // Will be improved by controller
            fieldType: field && field.type ? field.type.toLowerCase() : 'text',
            pdfFieldType: field && field.type ? field.type : null,
            pageNumber: annot.PageNumber || 1,
            filled: annot.getContents ? (annot.getContents() !== '' && annot.getContents() !== null) : false,
            subject: annot.Subject,
            participantEmail: null, // Will be set by controller in simulation mode
            participantColor: '#1976D2',
            x: rect ? rect.x1 : 0,
            y: rect ? rect.y1 : 0
          };
        });
        
        // Emit the ready event with field details
        this.$emit('guidedSigningReady', {
          totalFields: this.requiredFields.length,
          navigateNext: () => this.navigateToNextUnfilledField(),
          navigatePrevious: () => this.navigateToPreviousField(),
          fields: fieldsData
        });
        
        // Add listeners for UI updates
        instance.Core.documentViewer.addEventListener('scroll', this.onViewerScroll);
        instance.Core.documentViewer.addEventListener('zoomUpdated', this.onViewerScroll); // Update on zoom
        window.addEventListener('resize', this.onViewerScroll); // Update on browser resize

        // EXTRA: Robust scroll listener attachment
        const attachScrollListeners = () => {
            try {
                const iframeWindow = instance.UI.iframeWindow;
                if (iframeWindow) {
                    // 1. Window scroll
                    iframeWindow.addEventListener('scroll', this.onViewerScroll, { capture: true, passive: true }); 
                    
                    // 2. Document scroll
                    if (iframeWindow.document) {
                        iframeWindow.document.addEventListener('scroll', this.onViewerScroll, { capture: true, passive: true });
                        
                        // 3. Specific containers - try to find the actual scrolling element
                        const scrollSelectors = [
                            '.DocumentContainer', 
                            '[data-element="documentContainer"]', 
                            '.pageContainer', 
                            '#DocumentContainer',
                            '.ScrollView',
                            '#ScrollView'
                        ];
                        
                        scrollSelectors.forEach(selector => {
                            const els = iframeWindow.document.querySelectorAll(selector);
                            els.forEach(el => {
                                // Check if we already attached (custom prop)
                                if (!el._scrollAttached) {
                                    el.addEventListener('scroll', this.onViewerScroll, { capture: true, passive: true });
                                    el._scrollAttached = true;
                                    // console.log('✅ Attached scroll listener to container:', selector, el);
                                }
                            });
                        });
                    }
                }
            } catch (e) {
                console.warn('Error attaching scroll listeners:', e);
            }
        };
        
        // Try immediately and retry a few times to catch dynamic elements
        attachScrollListeners();
        setTimeout(attachScrollListeners, 1000);
        setTimeout(attachScrollListeners, 3000);
        
        // Add keyboard listener for Tab
        // We use capture phase on the iframe document to ensure we catch Tab before inputs consume it
        try {
            const iframeWindow = instance.UI.iframeWindow;
            if (iframeWindow && iframeWindow.document) {
                iframeWindow.document.addEventListener('keydown', this.handleIframeKeyDown, true);
            }
        } catch (e) {
            console.warn('Could not attach capture keydown listener:', e);
            // Fallback to PDFTron UI event
        instance.UI.addEventListener('keyDown', this.handleViewerKeyDown);
        }
        
        window.addEventListener('keydown', this.handleWindowKeyDown);
        
        // Apply visual differentiation to fields
        this.scheduleFieldStyling(500);
        this.checkAllFieldsComplete();
      } else {
        // No fields found - still emit event
        this.$emit('guidedSigningReady', {
          totalFields: 0,
          navigateNext: () => {},
          navigatePrevious: () => {},
          fields: []
        });
        
        // Force UI ready in simulation mode even if no fields found initially
        // The controller will likely push fields back via setRequiredFields
        if (this.simulationMode) {
           console.log('⚠️ No fields found initially, but enabling UI for simulation mode.');
           this.guidedSigningReady = true;
           
           // Add listeners just in case
           instance.Core.documentViewer.addEventListener('scroll', this.onViewerScroll);
           instance.UI.addEventListener('keyDown', this.handleViewerKeyDown);
           window.addEventListener('keydown', this.handleWindowKeyDown);
        }
      }
    },
    handleNavButtonClick(e) {
      if (e) e.stopPropagation();
      console.log('🔘 Nav Button Clicked. Text:', this.nextButtonText, 'Is Navigating:', this.isNavigating);
      
      if (this.isNavigating) return;
      this.isNavigating = true;
      setTimeout(() => { this.isNavigating = false; }, 500); // Debounce
      
      if (this.nextButtonText === 'Finish') {
        // Emit event to trigger the same behavior as the left panel Finish button
        this.$emit('finishButtonClicked');
        
        // Also emit allFieldsReviewed for backward compatibility
        this.$emit('allFieldsReviewed');
      } else {
        this.navigateToNextUnfilledField();
      }
    },
    handleIframeKeyDown(e) {
      if (e.keyCode === 9 && (this.simulationMode || this.signatureMode)) { // Tab
        console.log('Tab pressed in iframe (captured)');
        e.preventDefault();
        e.stopPropagation();
        this.navigateToNextUnfilledField();
      }
    },
    handleViewerKeyDown(e) {
      if (e.keyCode === 9) { // Tab
        console.log('Tab pressed in Viewer UI event');
        e.preventDefault();
        this.navigateToNextUnfilledField();
      }
    },
    handleWindowKeyDown(e) {
      if (e.keyCode === 9 && (this.simulationMode || this.signatureMode)) {
        e.preventDefault(); 
        this.navigateToNextUnfilledField();
      }
    },
      focusField(field) {
            if (!this.instance) {
                console.warn('⚠️ focusField called but instance is not ready yet');
                return false;
            }
            
            if (!this.instance.UI || !this.instance.UI.iframeWindow) {
                console.warn('⚠️ focusField called but iframe is not ready yet');
                return false;
            }
            
            if (!field) {
                console.warn('⚠️ focusField called with no field');
                return false;
            }
            
            const self = this;
        
        // Helper to find and focus input
        const attemptFocus = () => {
            try {
                const iframeWindow = self.instance.UI.iframeWindow;
                if (!iframeWindow) return false;
                const doc = iframeWindow.document;
                const name = field.fieldName;
                
                // Strategy 1: Exact name match (standard inputs)
                let inputs = doc.querySelectorAll(`input[name="${name}"], textarea[name="${name}"]`);
                
                // Strategy 2: Check based on the HTML structure provided by user
                // The div has ID "name_field" (matching the field name) and contains an input child
                if (!inputs || inputs.length === 0) {
                    const container = doc.getElementById(name);
                    if (container) {
                        inputs = container.querySelectorAll('input, textarea');
                        if (inputs.length > 0) {
                            console.log('✅ Found input inside container by ID:', name);
                        }
                    }
                }
                
                // Strategy 3: Manual name match (for text inputs)
                if (!inputs || inputs.length === 0) {
                     const allInputs = doc.querySelectorAll('input, textarea');
                     for (let i = 0; i < allInputs.length; i++) {
                         if (allInputs[i].name === name) {
                             inputs = [allInputs[i]];
                             break;
                         }
                     }
                }
                
                // Strategy 4: Handle Radio Groups specifically
                if (!inputs || inputs.length === 0) {
                    const radios = doc.querySelectorAll(`input[type="radio"][name="${name}"]`);
                    if (radios && radios.length > 0) {
                        let targetRadio = radios[0];
                        for (let i = 0; i < radios.length; i++) {
                            if (radios[i].checked) {
                                targetRadio = radios[i];
                                break;
                            }
                        }
                        inputs = [targetRadio];
                        console.log('✅ Found radio group inputs:', radios.length, 'focusing:', targetRadio.value);
                    }
                }
                
                // Strategy 5: Handle Select/Combo boxes (Dropdowns) and List Boxes
                if (!inputs || inputs.length === 0) {
                    // PDFTron might render dropdowns as select elements
                    const selects = doc.querySelectorAll(`select[name="${name}"]`);
                    if (selects && selects.length > 0) {
                        inputs = [selects[0]];
                        console.log('✅ Found select dropdown:', name);
                    }
                }
                
                // Strategy 6: Handle Checkboxes
                if (!inputs || inputs.length === 0) {
                    // Checkboxes are standard inputs but might be wrapped or have different attributes
                    // Try specific type select first
                    const checkboxes = doc.querySelectorAll(`input[type="checkbox"][name="${name}"]`);
                    if (checkboxes && checkboxes.length > 0) {
                        inputs = [checkboxes[0]];
                        console.log('✅ Found checkbox:', name);
                    }
                }
                
                // Strategy 7: Handle Signature Fields (Canvas/Divs)
                if (field.Subject === 'Signature') {
                    // Signatures are handled by selection logic below
                    return true;
                }
                
                // Strategy 8: Fallback for any other input type with matching name
                // This covers list boxes, combo boxes, etc. if they weren't caught above
                if (!inputs || inputs.length === 0) {
                    const fallbackInputs = doc.querySelectorAll(`[name="${name}"]`);
                    if (fallbackInputs && fallbackInputs.length > 0) {
                        // Filter for focusable elements
                        const focusable = Array.from(fallbackInputs).filter(el => {
                            return el.tabIndex >= 0 || 
                                   el.nodeName === 'INPUT' || 
                                   el.nodeName === 'SELECT' || 
                                   el.nodeName === 'TEXTAREA' || 
                                   el.nodeName === 'BUTTON';
                        });
                        
                        if (focusable.length > 0) {
                            inputs = [focusable[0]];
                            console.log('✅ Found fallback focusable element:', name, inputs[0].nodeName);
                        }
                    }
                }
                
                // Strategy 9: Check container ID again and look for ANY focusable child
                // This handles cases where the container ID matches the field name (like "name_field")
                // but the inner input doesn't have the name attribute.
                if (!inputs || inputs.length === 0) {
                    const container = doc.getElementById(name);
                    if (container) {
                        // Look for any focusable element inside
                        const focusableChildren = container.querySelectorAll('input, select, textarea, button, [tabindex="0"]');
                        if (focusableChildren && focusableChildren.length > 0) {
                            inputs = [focusableChildren[0]];
                            console.log('✅ Found focusable child in container:', name, inputs[0].nodeName);
                        }
                    }
                }
                
                if (inputs && inputs.length > 0) {
                     const input = inputs[0];
                     
                     // Check if input is disabled or hidden
                     const style = window.getComputedStyle(input);
                     if (style.display === 'none' || style.visibility === 'hidden' || input.disabled) {
                         console.log('⚠️ Input found but not interactive:', name);
                         return false;
                     }
                     
                     console.log('✅ Found and focusing input for field:', name);
                     
                     // Ensure the input is visible and enabled
                     input.style.pointerEvents = 'auto';
                     
                     // Focus and select
                     input.focus();
                     if (input.select) input.select();
                     
                     return true;
                }
                return false;
            } catch (e) {
                console.warn('Focus attempt failed:', e);
                return false;
            }
        };

        // Attempt 1: Try finding it directly (it might already exist)
        if (attemptFocus()) return;
        
        // Attempt 2: Use AnnotationManager to trigger actions
        // Based on logs, "Forcing edit mode" is called but focus fails
        // The provided HTML shows the input IS rendered inside a div with id="name_field"
        // but it might not be focused.
        
        console.log('🚀 Focusing Strategy: Input not found or not focused. Trying selection...');
        
        // Select the annotation
        this.instance.Core.annotationManager.selectAnnotation(field);
        
        // Wait slightly and try to find/focus again
        setTimeout(() => {
             if (attemptFocus()) return;
             
             // If still not found, try triggering the double click event
             console.log('🚀 Triggering double click event...');
             self.instance.Core.annotationManager.trigger('annotationDoubleClicked', field, { originalEvent: {} });
        }, 50);

        // Retry loop with adjusted timing
        const retries = [100, 300, 600, 1000];
        retries.forEach(delay => {
            setTimeout(() => {
                const success = attemptFocus();
                if (success) {
                    console.log(`✨ Focus successful after ${delay}ms`);
                } else if (delay === 1000) {
                    console.warn(`❌ Failed to focus field "${field.fieldName}" after multiple attempts`);
                }
            }, delay);
        });
    },
    onViewerScroll(e) {
      if (this.currentFieldIndex >= 0 && this.requiredFields && this.requiredFields[this.currentFieldIndex]) {
        this.updateArrowPosition(this.requiredFields[this.currentFieldIndex]);
      }
    },
    updateArrowPosition(field, retryCount = 0) {
      // console.log('🎯 updateArrowPosition ENTRY', { fieldName: field ? field.fieldName : 'null', retry: retryCount });
      
      if (!this.instance || !field) {
        return;
      }
      
      // Set the field label for display
      this.currentFieldLabel = field.fieldName || '';
      
      try {
        const documentViewer = this.instance.Core.documentViewer;
        
        // Get the annotation's rectangle in PDF coordinates
        const rect = field.getRect();
        if (!rect) {
          console.warn('🎯 Could not get annotation rect');
          return;
        }
        
        const pageNumber = field.PageNumber || field.pageNumber;
        
        // DOM-based calculation for bulletproof positioning
        const iframeDoc = this.instance.UI.iframeWindow.document;
        
        // Find the page container element
        // Try multiple selectors to find the page element
        let pageElement = null;
        const selectors = [
          `[data-page-number="${pageNumber}"]`,
          `.page[data-page-number="${pageNumber}"]`,
          `#pageContainer${pageNumber}`,
          `.pageContainer[data-page="${pageNumber}"]`
        ];
        
        for (const selector of selectors) {
          pageElement = iframeDoc.querySelector(selector);
          if (pageElement) break;
        }
        
        if (!pageElement) {
          // Fallback to finding by index
          const allPages = iframeDoc.querySelectorAll('.page, [class*="page"]');
          if (allPages.length >= pageNumber) {
            pageElement = allPages[pageNumber - 1];
          }
        }
        
        if (!pageElement) {
          // Retry if page not found (might not be rendered yet)
          if (retryCount < 5) {
            setTimeout(() => this.updateArrowPosition(field, retryCount + 1), 150);
          }
          return;
        }
        
        // Get the iframe element (the actual PDF viewer)
        const iframeElement = this.instance.UI.iframeWindow.frameElement;
        if (!iframeElement) return;
        
        // 1. Get Iframe position relative to viewport (Screen Absolute)
        const iframeRect = iframeElement.getBoundingClientRect();
        
        // 2. Get Page position relative to Iframe Viewport
        // Note: getBoundingClientRect inside iframe returns coords relative to the iframe's viewport
        const pageRect = pageElement.getBoundingClientRect();
        
        // 3. Calculate Field position relative to Page
        const zoom = documentViewer.getZoomLevel();
        // PDF Coords: (0,0) is usually bottom-left, but WebViewer normalizes Rects.
        // Based on logs, y1 seems to be Top.
        // Let's use the vertical center of the field.
        const fieldTopScaled = Math.min(rect.y1, rect.y2) * zoom;
        const fieldLeftScaled = rect.x1 * zoom;
        const fieldHeightScaled = Math.abs(rect.y2 - rect.y1) * zoom;
        const fieldCenterYScaled = fieldTopScaled + (fieldHeightScaled / 2);
        
        // 4. Calculate Absolute Screen Position
        // Top = IframeScreenTop + PageRectTop (relative to iframe) + FieldCenterY
        const absoluteTop = iframeRect.top + pageRect.top + fieldCenterYScaled;
        const absoluteLeft = iframeRect.left + pageRect.left + fieldLeftScaled;

        // Log debug info
        /*
        console.log('🎯 Arrow Pos Debug:', {
            iframeTop: iframeRect.top,
            pageRectTop: pageRect.top,
            fieldCenterY: fieldCenterYScaled,
            absoluteTop: absoluteTop,
            zoom: zoom,
            field: field.fieldName,
            containerTop: this.$el.getBoundingClientRect().top
        });
        */
        
        // Constants for arrow
        const arrowWidth = 60;
        const arrowHeight = 48;

        // Get container rect to calculate relative position
        const containerRect = this.$el.getBoundingClientRect();
        
        // Position arrow (using absolute positioning relative to container)
        const topVal = absoluteTop - containerRect.top - (arrowHeight / 2);
        const leftVal = absoluteLeft - containerRect.left - arrowWidth - 40; // 40px gap
        
        /*
        // REMOVED MANUAL DOM MANIPULATION
        // Rely on Vue reactivity to ensure state and DOM are in sync
        // Manual updates might be overwritten by Vue or causing conflicts
        */
        
        // console.log('🎯 Updating arrowPosition state:', topVal, leftVal);
        
        this.arrowPosition = {
          top: topVal,
          left: leftVal
        };
        
        if (!this.showArrow) {
            this.$nextTick(() => {
              this.showArrow = true;
            });
        }
        
      } catch (e) {
        console.error('🎯 updateArrowPosition error:', e);
      }
    },
    updateButtonState() {
       if (this.currentFieldIndex === -1) {
         // Initial state - check if there are any unfilled fields
         const firstUnfilled = this.getFirstUnfilledField();
         
         if (!firstUnfilled) {
           // No unfilled fields found
           // In signature mode, if everything is already completed on load, show "Completed"
           // In simulation mode, show "Finish" (normal behavior)
           if (this.signatureMode) {
             this.nextButtonText = "Completed";
             console.log('Updating button state. State: Initial, All fields already completed, Button: Completed');
           } else {
             this.nextButtonText = "Finish";
             console.log('Updating button state. State: Initial, No unfilled fields, Button: Finish');
           }
         } else {
           this.nextButtonText = "Start";
           console.log('Updating button state. State: Initial, Button:', this.nextButtonText);
         }
       } else {
         // We're at a field - check if there's a next unfilled field
         const nextUnfilled = this.getNextUnfilledField();
         if (nextUnfilled) {
           this.nextButtonText = "Next";
           console.log('Updating button state. Current field index:', this.currentFieldIndex, 
                      'Current field:', this.requiredFields[this.currentFieldIndex] ? this.requiredFields[this.currentFieldIndex].fieldName : 'unknown',
                      'Next unfilled:', nextUnfilled.fieldName, 'Button: Next');
         } else {
           this.nextButtonText = "Finish";
           this.showArrow = false;
           console.log('Updating button state. No more unfilled fields. Button: Finish');
         }
       }
       
       // Emit button text to parent component
       this.$emit('updateNextButtonText', this.nextButtonText);
    },
    getNextUnfilledField() {
        if (!this.requiredFields || this.requiredFields.length === 0) return null;
        
        let relevantFields = this.requiredFields;
        
        // Filter by participant ONLY in Simulation Mode (or if not in Signature Mode)
        // In Signature Mode, the controller has already filtered the requiredFields list
        // to include only what is relevant for the recipient (including unassigned fields),
        // so we should NOT filter again here (which would strip out unassigned fields).
        if (!this.signatureMode && this.activeParticipantEmail && this.activeParticipantEmail !== 'all') {
             relevantFields = relevantFields.filter(f => {
                const pEmail = f.getCustomData('participantEmail') || f.getCustomData('recipientEmail');
                const pId = f.getCustomData('participantId') || f.getCustomData('recipientIndex');
                return pEmail === this.activeParticipantEmail || pId === this.activeParticipantEmail;
             });
        }
        
        const pdfFields = this.instance.Core.annotationManager.getFieldManager().getFields();
        const pdfFieldsMap = {};
        Object.keys(pdfFields).forEach(k => pdfFieldsMap[pdfFields[k].name] = pdfFields[k]);
        
        // Helper to check if a field is relevant and unfilled
        const isRelevantAndUnfilled = (field) => {
            if (relevantFields.some(rf => rf.Id === field.Id)) {
                return !this.isFieldFilled(field, pdfFieldsMap);
            }
            return false;
        };

        // 1. Search forward from current + 1
        for (let i = this.currentFieldIndex + 1; i < this.requiredFields.length; i++) {
            if (isRelevantAndUnfilled(this.requiredFields[i])) {
                return this.requiredFields[i];
            }
        }
        
        // 2. Cycle: Search from start to current
        // This implements "cycle back" logic if we reached the end but missed earlier fields
        for (let i = 0; i <= this.currentFieldIndex; i++) {
             // If it's the current field and we are here, we probably just arrived or are editing it.
             // But if it's unfilled, it IS a valid target.
             // However, we usually want to find the *next* one.
             // If we are at index 0, and it's unfilled, loop 1 checks 1..end. Loop 2 checks 0..0.
             // So we will return 0. Then navigateToNextUnfilledField moves to 0.
             // This is acceptable behavior (stay on current if it's the only one or if we cycled back to it).
             
             if (isRelevantAndUnfilled(this.requiredFields[i])) {
                return this.requiredFields[i];
            }
        }
        
        return null;
    },
    navigateToNextUnfilledField() {
      console.log('🚀 navigateToNextUnfilledField called. Current Index:', this.currentFieldIndex);
      
      // Special case: if currentFieldIndex is -1, this is the "Start" button
      // Navigate to the FIRST unfilled field (index 0) without advancing
      if (this.currentFieldIndex === -1) {
        const firstField = this.getFirstUnfilledField();
        console.log('🚀 Start Mode. First field found:', firstField ? firstField.fieldName : 'None');
        
        if (firstField) {
          const index = this.requiredFields.indexOf(firstField);
          console.log('🚀 Setting index to:', index);
          this.currentFieldIndex = index;
          this.navigateToField(index);
          this.updateButtonState(); // Update button to show "Next"
          return;
        }
      }
      
      // Normal case: find the next unfilled field after the current one
      const nextField = this.getNextUnfilledField();
      console.log('🚀 Next Mode. Next field found:', nextField ? nextField.fieldName : 'None');
      
      if (nextField) {
        const index = this.requiredFields.indexOf(nextField);
        console.log('🚀 Setting index to:', index);
        this.currentFieldIndex = index;
        this.navigateToField(index);
        this.updateButtonState();
      } else {
        this.nextButtonText = "Finish";
        this.showArrow = false;
        this.$emit('updateNextButtonText', this.nextButtonText);
        this.checkAllFieldsComplete();
      }
    },
    getFirstUnfilledField() {
      // Get the first unfilled field from the beginning (for "Start" button)
      if (!this.requiredFields || this.requiredFields.length === 0) return null;
      
      let relevantFields = this.requiredFields;
      
      // Filter by participant ONLY in Simulation Mode (or if not in Signature Mode)
      // In Signature Mode, the controller has already filtered the requiredFields list
      // to include only what is relevant for the recipient (including unassigned fields),
      // so we should NOT filter again here (which would strip out unassigned fields).
      if (!this.signatureMode && this.activeParticipantEmail && this.activeParticipantEmail !== 'all') {
           relevantFields = this.requiredFields.filter(f => {
              const pEmail = f.getCustomData('participantEmail') || f.getCustomData('recipientEmail');
              const pId = f.getCustomData('participantId') || f.getCustomData('recipientIndex');
              return pEmail === this.activeParticipantEmail || pId === this.activeParticipantEmail;
           });
      }
      
      const pdfFields = this.instance.Core.annotationManager.getFieldManager().getFields();
      const pdfFieldsMap = {};
      Object.keys(pdfFields).forEach(k => pdfFieldsMap[pdfFields[k].name] = pdfFields[k]);
      
      // Search from the beginning
      for (let i = 0; i < this.requiredFields.length; i++) {
          const field = this.requiredFields[i];
          // Check if this field is relevant for the current participant
          if (relevantFields.some(rf => rf.Id === field.Id)) {
              // Check if it's unfilled
              if (!this.isFieldFilled(field, pdfFieldsMap)) {
                  return field;
              }
          }
      }
      
      return null;
    },
    navigateToField(index) {
      if (index < 0 || index >= this.requiredFields.length || !this.instance) {
        return;
      }
      
      const field = this.requiredFields[index];
      const annotationManager = this.instance.Core.annotationManager;
      
      // Use built-in jump if available (handles pages and scrolling)
      if (annotationManager.jumpToAnnotation) {
        annotationManager.jumpToAnnotation(field);
        
        // Nudge scroll to ensure arrow (to the left of field) is visible
        setTimeout(() => {
           try {
             const scrollView = this.instance.Core.documentViewer.getScrollViewElement();
             if (scrollView) {
                // Scroll left slightly to make room for the arrow indicator
                const currentScroll = scrollView.scrollLeft;
                if (currentScroll > 0) {
                   scrollView.scrollLeft = Math.max(0, currentScroll - 100);
                }
             }
           } catch (e) { /* ignore */ }
        }, 100);
      } else {
        // Fallback
        const documentViewer = this.instance.Core.documentViewer;
        documentViewer.setCurrentPage(field.PageNumber);
        
        setTimeout(() => {
          if (documentViewer.displayPageLocation) {
             documentViewer.displayPageLocation(field.PageNumber, field.X, field.Y + (field.Height/2));
          }
        }, 50);
      }
      
      // Select the annotation to highlight it
      annotationManager.selectAnnotation(field);
      
      // Update arrow after a delay to allow scroll to finish/settle
      // We use a slightly longer timeout to ensure layout is stable
      setTimeout(() => {
        // Let updateArrowPosition handle setting showArrow to true on success
        this.updateArrowPosition(field);
        // Attempt to focus the field for typing
        this.focusField(field);
      }, 200);
      
      this.$emit('fieldNavigated', {
        index: index,
        total: this.requiredFields.length,
        field: field
      });
    },
    navigateToPreviousField() {
      if (this.currentFieldIndex > 0) {
        this.currentFieldIndex--;
        this.navigateToField(this.currentFieldIndex);
      }
    },
    addNavigationUI(instance) {
      // Add custom navigation buttons for guided signing
      // This method can be used to customize the PDFTron UI if needed
      // The guidedSigningReady event is now emitted from setupGuidedSigning
    },
    async completeSignature() {
      if (!this.signatureMode || !this.signatureRequestId) {
        throw new Error('Not in signature mode');
      }
      
      try {
        const {Core} = this.instance;
        const doc = Core.documentViewer.getDocument();
        const xfdfString = await Core.annotationManager.exportAnnotations();
        const data = await doc.getFileData({xfdfString});
        
        if (!data) {
          throw new Error("Unable to retrieve PDF data");
        }
        
        // Emit completion event with signed PDF data
        this.$emit('signatureCompleted', {
          signatureRequestId: this.signatureRequestId,
          xfdfString: xfdfString,
          pdfData: data
        });
        
        return true;
      } catch (error) {
        console.error('Error completing signature:', error);
        throw error;
      }
    },
    validateRequiredFields() {
      if (!this.signatureMode || this.requiredFields.length === 0) {
        return true;
      }
      
      // Check if all required fields have been filled
      const annotationManager = this.instance.Core.annotationManager;
      const fieldManager = annotationManager.getFieldManager();
      
      for (let field of this.requiredFields) {
        // Check if field has content
        if (field.Subject === 'Signature') {
          // Check if signature annotation has been drawn/added
          // Signatures can have paths (drawn), image data, or other content
          const hasPaths = field.getPaths && field.getPaths().length > 0;
          const hasImageData = field.ImageData && field.ImageData.length > 0;
          const hasWidth = field.Width > 0;
          
          if (!hasPaths && !hasImageData && !hasWidth) {
            return false;
          }
        } else if (field.Subject === 'Free Text') {
          // Check if text field has content
          if (!field.getContents() || field.getContents().trim() === '') {
            return false;
          }
        }
      }
      
      return true;
    },
    checkAllFieldsComplete() {
      // Check if all required fields are complete
      if (!this.signatureMode || this.requiredFields.length === 0) {
        return;
      }
      
      const allComplete = this.validateRequiredFields();
      
      // If all fields are complete and we haven't already notified, emit the event
      if (allComplete && !this.allFieldsCompleted) {
        this.allFieldsCompleted = true;
        this.$emit('allFieldsReviewed');
      }
      
      // Update field styling to reflect filled/unfilled state
      if (this.signatureMode) {
        this.scheduleFieldStyling(100);
      }
    },
    scheduleFieldStyling(delay = 200) {
      // Debounced wrapper for applyFieldStyling to prevent race conditions
      // Cancels any pending styling update and schedules a new one
      if (this.fieldStylingTimeout) {
        clearTimeout(this.fieldStylingTimeout);
      }
      
      this.fieldStylingTimeout = setTimeout(() => {
        this.applyFieldStyling();
        this.fieldStylingTimeout = null;
      }, delay);
    },
    applyFieldStyling() {
      // Apply visual differentiation between required and non-required fields
      console.log('🎨 applyFieldStyling called - starting field styling...');
      
        if (!this.instance) {
          console.warn('⚠️ applyFieldStyling called but instance is not ready yet');
          return;
        }
        
        if (!this.instance.Core) {
          console.warn('⚠️ applyFieldStyling called but instance.Core is not ready yet');
          return;
        }
        
        if (this.isProgrammaticUpdate) {
          console.log('⚠️ applyFieldStyling skipped - programmatic update already in progress');
        return;
      }
      
      // Only apply in signature or simulation mode
      if (!this.signatureMode && !this.simulationMode) {
        console.log('⚠️ applyFieldStyling skipped - not in signature or simulation mode');
        return;
      }
      
      console.log('✅ applyFieldStyling proceeding - setting isProgrammaticUpdate flag');
        this.isProgrammaticUpdate = true; // Set flag to prevent loops
      
      try {
        // Cache all instance references at the start to avoid issues if instance changes during execution
        const instance = this.instance;
        if (!instance || !instance.Core) {
          console.warn('⚠️ Instance or Core became unavailable during applyFieldStyling');
          return;
        }
        
        const annotationManager = instance.Core.annotationManager;
        if (!annotationManager) {
          console.warn('⚠️ annotationManager not available');
          return;
        }
        
        const fieldManager = annotationManager.getFieldManager();
        const annotations = annotationManager.getAnnotationsList();
        const Color = instance.Core.Annotations.Color;
        const WidgetFlags = instance.Core.Annotations.WidgetFlags;
        
        // Get all PDF form fields to check required status
        const pdfFieldsArray = fieldManager.getFields();
        
        // Convert to a lookup object keyed by field name
        const pdfFields = {};
        for (let i = 0; i < pdfFieldsArray.length; i++) {
          const field = pdfFieldsArray[i];
          if (field && field.name) {
            pdfFields[field.name] = field;
          }
        }
        
        const fieldStatuses = [];
        const annotationsToUpdate = [];
        
        // Prevent hiding active signatures that might be currently interacting
        // We need a way to distinguish between "Comments" and functional annotations
        // Filter strictly: if it has no fieldName, and isn't a Signature or FreeText we want, hide it.
        
        annotations.forEach(annot => {
          // 1. Determine if this is a "functional" annotation we want to keep
          // - Widgets (Form Fields)
          // - Signatures (Drawn signatures)
          // Note: FreeText comments should already be deleted at document load time
          
          const isWidget = annot.isWidget || annot.getCustomData('isWidget') || annot.fieldName || (annot.getCustomData && annot.getCustomData('fieldType'));
          const isSignature = annot.Subject === 'Signature';
          
          // We also keep annotations that are currently being created/edited to avoid disappearing while drawing
          const isBeingEdited = annotationManager.isAnnotationSelected(annot);

          // If it's NOT a functional component (widget or signature), hide it
          if (!isWidget && !isSignature && !isBeingEdited) {
              if (!annot.Hidden) {
                  annot.Hidden = true;
                  annotationsToUpdate.push(annot);
              }
              return;
          }
          
          // If it IS functional (widget or signature), ensure it is visible
          if (annot.Hidden && (isWidget || isSignature)) {
              annot.Hidden = false;
              annotationsToUpdate.push(annot);
          }

          // Skip styling calculation for non-widgets (except Signatures)
          if (!annot.fieldName && !isSignature) {
            return;
          }
          
          // Determine if this field is required
          let isRequired = false;
          let explicitStatusSet = false;
          
          // Check custom data for required status FIRST - this overrides everything else
          // This allows us to explicitly mark fields as "not required" even if they are signatures
          const customRequired = annot.getCustomData('required');
          
          if (customRequired === 'true') {
            isRequired = true;
            explicitStatusSet = true;
          } else if (customRequired === 'false') {
            isRequired = false;
            explicitStatusSet = true;
          } else if (this.signatureMode && this.requiredFields && this.requiredFields.length > 0) {
              // In SIGN MODE with a filtered list: This list is the SINGLE SOURCE OF TRUTH for the current user
              // If it's in the list, it's required. If not, it's NOT required (for this user).
                if (this.requiredFields.some(f => {
                  return (f.Id === annot.Id) || 
                         (f.fieldName && annot.fieldName && f.fieldName === annot.fieldName);
                })) {
                  isRequired = true;
              } else {
                isRequired = false;
                }
              explicitStatusSet = true;
              }
              
          // FALLBACK: Only check default flags if no explicit status is set
          if (!explicitStatusSet) {
              // Check PDF field flags for required status
              if (!isRequired) {
                  let field = null;
                  if (annot.fieldName && pdfFields[annot.fieldName]) {
                    field = pdfFields[annot.fieldName];
                    
                    if (field.flags && typeof field.flags.get === 'function') {
                      if (field.flags.get(WidgetFlags.REQUIRED)) {
                        isRequired = true;
                      }
                    } else if (field.flags && typeof field.flags === 'number') {
                      // Bitmask check: Required flag is bit 1 (value 2)
                      if (field.flags & 2) {
                        isRequired = true;
                      }
                    }
                    
                    // Signature fields are always required in signature/simulation mode
                    if (field.type === 'Sig') {
                      isRequired = true;
                    }
                  }
                  
                  // Signature annotations are always required
                  if (annot.Subject === 'Signature') {
                    isRequired = true;
                  }
              }
          }
          
          if (isRequired) {
            // Check if field is filled
            const isFilled = this.isFieldFilled(annot, pdfFields);
            
            // Debug logging for signature fields
            if (annot.fieldName && annot.fieldName.toLowerCase().includes('signature')) {
              console.log(`🔍 Checking filled status for "${annot.fieldName}":`, {
                isFilled: isFilled,
                annotSubject: annot.Subject,
                fieldType: pdfFields[annot.fieldName] ? pdfFields[annot.fieldName].type : 'unknown'
              });
            }
            
            // Track field status
            fieldStatuses.push({
              id: annot.Id,
              fieldName: annot.fieldName,
              isFilled: isFilled
            });
            
            if (isFilled) {
              // Filled required field - normal blue border, Light Blue background
              annot.Opacity = 1.0;
              
              // Always use blue for filled required fields (unless participant color specified)
              const participantColor = annot.getCustomData('participantColor');
              if (participantColor) {
                annot.StrokeColor = new Color(participantColor);
              } else {
                annot.StrokeColor = new Color(33, 150, 243); // Brighter blue
              }
              annot.StrokeThickness = 1; // Back to 1px
              if (annot.BorderStyle) annot.BorderStyle.width = 1;
              
              // Use OPAQUE Light Blue to indicate required field
              annot.FillColor = new Color(235, 245, 255, 1); 
            } else {
              // Unfilled required field - red border, Light Blue background
              annot.Opacity = 1.0;
              annot.StrokeColor = new Color(211, 47, 47); // Red color
              annot.StrokeThickness = 1; // Back to 1px
              if (annot.BorderStyle) annot.BorderStyle.width = 1;
              
              // Use OPAQUE Light Blue so the user knows it's a required field area
              annot.FillColor = new Color(235, 245, 255, 1);
            }
          } else {
            // Non-required field - OPAQUE very light gray background
            annot.Opacity = 1.0;
            
            // Light gray border
            annot.StrokeColor = new Color(189, 189, 189); // Light gray border
            annot.StrokeThickness = 1;
            if (annot.BorderStyle) annot.BorderStyle.width = 1;
            
            // Opaque very light gray to distinguish from blue required fields
            annot.FillColor = new Color(248, 248, 248, 1); 
          }
          
          // Mark for update
          annotationsToUpdate.push(annot);
        });
        
        // Batch update annotations
        if (annotationsToUpdate.length > 0) {
            // Update the properties internally
            annotationsToUpdate.forEach(annot => annotationManager.updateAnnotation(annot));
            // Redraw only the modified annotations
            annotationManager.drawAnnotationsFromList(annotationsToUpdate);
        }
        
        // Emit field status updates
        if (fieldStatuses.length > 0) {
          // console.log('📤 Emitting fieldStatusChanged');
          this.$emit('fieldStatusChanged', fieldStatuses);
        }
        
      } catch (error) {
        console.error('Error applying field styling:', error);
      } finally {
        // Reset flag after a short delay to allow current event loop to finish
        setTimeout(() => {
            this.isProgrammaticUpdate = false;
        }, 50);
      }
    },
    isFieldFilled(annot, pdfFields) {
      // Check if a field has been filled by the user (not blank)
      try {
        // Guard against instance being unavailable
        if (!this.instance || !this.instance.Core) {
          return false;
        }
        
        // For signature annotations
        if (annot.Subject === 'Signature') {
          // Check if signature has been drawn/added
          const hasPaths = annot.getPaths && annot.getPaths().length > 0;
          const hasImageData = annot.ImageData && annot.ImageData.length > 0;
          if (hasPaths || hasImageData) return true;
          
          // Check for associated signature annotation (visual check)
          // Check for ANY annotation that intersects with this widget (overlap)
          const annotRect = annot.getRect();
          const innerAnnot = this.instance.Core.annotationManager.getAnnotationsList().find(a => {
            if (a.Subject !== 'Signature' && a.Subject !== 'Stamp' && a.Subject !== 'Ink') return false;
            if (a === annot) return false; // Don't check against self
            
            const aRect = a.getRect();
            // Check for intersection
            return !(aRect.x1 > annotRect.x2 || 
                     aRect.x2 < annotRect.x1 || 
                     aRect.y1 > annotRect.y2 || 
                     aRect.y2 < annotRect.y1);
          });
          
          if (innerAnnot) return true;
          
          return false;
        }
        
        // For free text annotations
        if (annot.Subject === 'Free Text') {
          const content = annot.getContents ? annot.getContents() : '';
          return content && content.trim() !== '';
        }
        
        // Check if we can resolve the field from the manager first (Most Reliable)
        if (annot.fieldName && pdfFields[annot.fieldName]) {
          const field = pdfFields[annot.fieldName];
          
          // Check if this is a signature field
          if (field.type === 'Sig') {
            const fieldName = annot.fieldName || 'unknown';
            
            // Method 1: Check Dp property (signature image data)
            // This is the most reliable indicator that a signature has been applied
            if (annot.Dp && annot.Dp.filename) {
              console.log(`✅ "${fieldName}" filled: has Dp.filename`);
              return true;
            }
            
            // Method 2: Check we property (signature drawing data)
            if (annot.we && annot.we.dataString) {
              console.log(`✅ "${fieldName}" filled: has we.dataString`);
              return true;
            }
            
            // Method 3: Check if annotation has image data via getImageData()
            const imageData = annot.getImageData && annot.getImageData();
            if (imageData) {
              console.log(`✅ "${fieldName}" filled: has imageData`);
              return true;
            }
            
            // Method 4: Check contents
            const contents = annot.getContents && annot.getContents();
            if (contents && contents !== '') {
              console.log(`✅ "${fieldName}" filled: has contents="${contents}"`);
              return true;
            }
            
            // Method 5: Check field value
            if (field.value && field.value !== '') {
              console.log(`✅ "${fieldName}" filled: field.value="${field.value}"`);
              return true;
            }
            
            // Method 6: Check paths
            const hasPaths = annot.getPaths && annot.getPaths().length > 0;
            if (hasPaths) {
              console.log(`✅ "${fieldName}" filled: has paths`);
              return true;
            }
            
            // REMOVED: Method 7 & 8 - appearance streams can contain "Sign here" placeholders
            // They are not reliable indicators of actual signatures
            
            // Method 7: Visual check - look for overlapping signature annotations
            const annotRect = annot.getRect();
            const allAnnots = this.instance.Core.annotationManager.getAnnotationsList();
            
            // Debug: log all annotations near this signature field
            console.log(`🔍 Checking for overlapping annotations for "${fieldName}":`, {
              widgetRect: annotRect,
              totalAnnotations: allAnnots.length,
              widgetPageNumber: annot.PageNumber
            });
            
            // Find all annotations on the same page
            const samePageAnnots = allAnnots.filter(a => a.PageNumber === annot.PageNumber);
            console.log(`  📄 Annotations on same page (${annot.PageNumber}):`, samePageAnnots.length);
            
            // Check each annotation's subject
            const annotsBySubject = {};
            samePageAnnots.forEach(a => {
              const subject = a.Subject || 'Unknown';
              if (!annotsBySubject[subject]) {
                annotsBySubject[subject] = 0;
              }
              annotsBySubject[subject]++;
            });
            console.log(`  📊 Annotations by Subject:`, annotsBySubject);
            
            const innerAnnot = allAnnots.find(a => {
              // Signature fields can have various annotation types overlapping them:
              // - Signature, Stamp, Ink (traditional signature types)
              // - Rectangle, Note (drawn/typed signatures in PDFTron)
              // We exclude Widget annotations (form fields themselves)
              if (a.Subject === 'Widget') return false;
              if (a === annot) return false;
              if (a.PageNumber !== annot.PageNumber) return false;
              
              // CRITICAL: Exclude field overlay rectangles (colored borders)
              // These are system-generated and not actual signatures
              const customData = a.getCustomData && a.getCustomData('isFieldOverlay');
              if (customData === 'true' || customData === true) {
                return false; // Skip field overlay rectangles
              }
              
              // Also check for field label notes
              const isFieldLabel = a.getCustomData && a.getCustomData('isFieldLabel');
              if (isFieldLabel === 'true' || isFieldLabel === true) {
                return false; // Skip field label notes
              }
              
              const aRect = a.getRect();
              const intersects = !(aRect.x1 > annotRect.x2 || aRect.x2 < annotRect.x1 || aRect.y1 > annotRect.y2 || aRect.y2 < annotRect.y1);
              
              if (intersects) {
                console.log(`  ✅ Found overlapping annotation:`, {
                  subject: a.Subject,
                  rect: aRect,
                  widgetRect: annotRect
                });
              }
              
              return intersects;
            });
            if (innerAnnot) {
              console.log(`✅ "${fieldName}" filled: has overlapping signature annotation`);
              return true;
            }
            
            console.log(`❌ "${fieldName}" NOT filled: no signature data found`);
            return false;
          }
          
          // For text fields
          if (field.type === 'Tx') {
            const value = field.value;
            if (value !== null && value !== undefined && value !== '') {
              const strValue = String(value).trim();
              if (strValue !== '') {
                return true;
              }
            }
            return false;
          }
          
          // For button fields (checkboxes and radio buttons)
          if (field.type === 'Btn') {
            const value = field.value;
            
            // Detect if it's a radio by checking if it has multiple widgets
            const isRadio = field.widgets && field.widgets.length > 1;
            
            if (annot.fieldName === 'contact_method') {
              console.log('🔘 Checking radio button:', {
                fieldName: annot.fieldName,
                isRadio: isRadio,
                widgetCount: field.widgets ? field.widgets.length : 0,
                fieldValue: value
              });
            }
            
            // For radio buttons and checkboxes, check the field value first
            // IMPORTANT: 'Off' means unchecked/unselected
            if (value !== null && value !== undefined && value !== '' && value !== 'Off') {
              if (annot.fieldName === 'contact_method') {
                console.log('🔘   Field value indicates SELECTED:', value);
              }
              return true;
            }
            
            if (annot.fieldName === 'contact_method') {
              console.log('🔘   Field value indicates NOT selected:', value);
            }
            return false;
          }
          
          // For choice fields (dropdowns and list boxes)
          if (field.type === 'Ch') {
            const value = field.value;
            if (value !== null && value !== undefined && value !== '') {
              return true;
            }
            return false;
          }
        }
        
        // Fallback: Check annotation's own value property if field manager didn't find it
        // Only do this if we didn't match a field above, OR for non-field widgets
        if (annot.value !== undefined && annot.value !== null) {
          if (typeof annot.value === 'string') {
            if (annot.value.trim() !== '') {
              return true;
            }
          } else if (annot.value !== '') {
            return true;
          }
        }
        
        // Check annotation getContents
        if (annot.getContents && typeof annot.getContents === 'function') {
          const content = annot.getContents();
          if (content && typeof content === 'string' && content.trim() !== '') {
            return true;
          }
        }
        
        return false;
      } catch (error) {
        console.error('Error checking if field is filled:', error);
        return false;
      }
    },
    async getSimulationResults() {
      // Method for simulation mode to get what would be saved without actually saving
      if (!this.simulationMode) {
        console.warn('getSimulationResults called but not in simulation mode');
        return null;
      }
      
      try {
        const Core = this.instance.Core;
        const doc = Core.documentViewer.getDocument();
        const xfdfString = await Core.annotationManager.exportAnnotations();
        
        return {
          xfdfString: xfdfString,
          fileName: this.file ? this.file.name : 'unknown',
          vaultName: this.vault ? this.vault.name : 'unknown',
          fieldCount: this.requiredFields.length,
          timestamp: new Date().toISOString()
        };
      } catch (error) {
        console.error('Error getting simulation results:', error);
        throw error;
      }
    },
    async getXFDFString() {
      // Export XFDF string with all annotations and form data
      if (!this.instance || !this.instance.Core) {
        console.warn('PDF viewer not ready');
        return '';
      }
      
      try {
        const xfdfString = await this.instance.Core.annotationManager.exportAnnotations();
        return xfdfString;
      } catch (error) {
        console.error('Error exporting XFDF:', error);
        return '';
      }
    },
    async getFieldsInformation() {
      // Extract detailed field information for both modes
      console.log('🔍 ===== getFieldsInformation START =====');
      console.log('🔍 Mode:', this.signatureMode ? 'SIGN' : 'SIMULATION');
      
      if (!this.instance || !this.instance.Core) {
        console.warn('PDF viewer not ready');
        return null;
      }
      
      try {
        const annotationManager = this.instance.Core.annotationManager;
        const fieldManager = annotationManager.getFieldManager();
        
        // IMPORTANT: Wait a moment for FieldManager to fully load field values from PDF
        // Field values are loaded asynchronously from the PDF document
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Log current field values for debugging
        const initialFieldValues = fieldManager.getFields();
        const fieldValuesLog = [];
        Object.keys(initialFieldValues).forEach(fieldName => {
          const field = initialFieldValues[fieldName];
          if (field && field.value !== undefined && field.value !== '' && field.value !== 'Off') {
            fieldValuesLog.push({
              name: fieldName,
              value: field.value,
              type: field.type
            });
          }
        });
        if (fieldValuesLog.length > 0) {
          console.log('📝 Current PDF field values:', fieldValuesLog);
        } else {
          console.log('📝 No pre-filled field values detected in PDF');
        }
        
        // Determine default participant email for unassigned fields
        // In sign mode, unassigned fields should remain unassigned (null) rather than defaulting to the recipient
        // This ensures they are correctly identified as "Not Required" and filtered out
        const defaultParticipantEmail = this.signatureMode ? null : 'signer@simulation.local';
        if (this.signatureMode) {
          console.log('📧 Sign mode: Unassigned fields will default to null (not assigned to recipient)');
        }
        
        // Apply field assignments from JSON (field values come from the PDF itself)
        if (this.signatureMode && this.fieldAssignments) {
          console.log('📝 Sign mode: Applying field assignments from JSON (Strict Recipient Mode)...');
          console.log('📝 Field assignments:', this.fieldAssignments);
          
          const annotations = annotationManager.getAnnotationsList();
          let assignedCount = 0;
          
          // Create Set of assigned field names for fast lookup
          const assignedFieldNames = new Set(Object.keys(this.fieldAssignments));
          
          // Iterate ALL widget annotations to apply assignments AND handle unassigned fields
          annotations.forEach(annot => {
            // Skip non-widgets
            if (annot.Subject !== 'Widget' || !annot.fieldName) {
                return;
            }
            
            const fieldName = annot.fieldName;
            
            if (assignedFieldNames.has(fieldName)) {
                // Case 1: Field IS assigned to a recipient
                const assignment = this.fieldAssignments[fieldName];
                
                // Apply participant metadata directly
                if (assignment.participantEmail) {
                  annot.setCustomData('participantEmail', assignment.participantEmail);
                }
                if (assignment.participantId) {
                  annot.setCustomData('participantId', assignment.participantId);
                }
                if (assignment.participantName) {
                  annot.setCustomData('participantName', assignment.participantName);
                }
                if (assignment.participantColor) {
                  annot.setCustomData('participantColor', assignment.participantColor);
                }
                if (assignment.isOrganization !== undefined) {
                  annot.setCustomData('isOrganization', String(assignment.isOrganization));
                }
                // CRITICAL FIX: Use 'required' key (not 'isRequired') to match getCustomData('required')
                if (assignment.isRequired !== undefined) {
                  annot.setCustomData('required', String(assignment.isRequired));
                }
                
                assignedCount++;
            } else {
                // Case 2: Field is NOT assigned to any recipient
                // In strict recipient mode, unassigned signatures should NOT be required
                // Check if it's a signature field
                const field = fieldManager.getField(fieldName);
                let isSignature = (field && field.type === 'Sig');
                
                // Fallback check for signature annotation subject if field manager lookup fails
                if (!isSignature && annot.Subject === 'Signature') {
                    isSignature = true;
                }
                
                if (isSignature) {
                    // Explicitly mark unassigned signature as NOT required
                    // This overrides the default "signatures are always required" logic
                    annot.setCustomData('required', 'false');
                    // Also clear participant data to be safe
                    annot.setCustomData('participantEmail', '');
                    annot.setCustomData('participantId', '');
                    annot.setCustomData('participantName', 'Not Required');
                    annot.setCustomData('participantColor', '#9E9E9E');
                }
            }
          });
          
          console.log('✅ Applied assignments to', assignedCount, 'fields');
        } else if (this.signatureMode && this.requiredFieldsXfdf) {
          // BACKWARD COMPATIBILITY: Extract field assignments from XFDF without importing
          // Old signature requests only have XFDF, not the field_assignments JSON
          console.log('⚠️ Using legacy XFDF to extract field assignments (backward compatibility)');
          console.log('📝 XFDF length:', this.requiredFieldsXfdf.length, 'characters');
          
          // Parse XFDF to extract custom data (participant assignments) WITHOUT importing
          // This avoids corrupted appearance streams and doesn't overwrite field values
          try {
            const parser = new DOMParser();
            const xfdfDoc = parser.parseFromString(this.requiredFieldsXfdf, 'text/xml');
            const widgetAnnots = xfdfDoc.querySelectorAll('widget');
            
            const annotations = annotationManager.getAnnotationsList();
            let assignedCount = 0;
            
            widgetAnnots.forEach(widgetNode => {
              const fieldName = widgetNode.getAttribute('field');
              if (!fieldName) return;
              
              // Extract custom data from the widget
              const customDataNodes = widgetNode.querySelectorAll('trn-custom-data');
              const customData = {};
              customDataNodes.forEach(node => {
                const key = node.getAttribute('key');
                const value = node.getAttribute('value');
                if (key && value) {
                  customData[key] = value;
                }
              });
              
              // Find matching annotations in the PDF
              const fieldAnnotations = annotations.filter(annot => 
                annot.Subject === 'Widget' && annot.fieldName === fieldName
              );
              
              if (fieldAnnotations.length > 0 && Object.keys(customData).length > 0) {
                fieldAnnotations.forEach(annot => {
                  // Apply participant metadata from XFDF custom data
                  if (customData.participantEmail) {
                    annot.setCustomData('participantEmail', customData.participantEmail);
                  }
                  if (customData.participantId) {
                    annot.setCustomData('participantId', customData.participantId);
                  }
                  if (customData.participantName) {
                    annot.setCustomData('participantName', customData.participantName);
                  }
                  if (customData.participantColor) {
                    annot.setCustomData('participantColor', customData.participantColor);
                  }
                  if (customData.isOrganization) {
                    annot.setCustomData('isOrganization', customData.isOrganization);
                  }
                  if (customData.isRequired) {
                    annot.setCustomData('isRequired', customData.isRequired);
                  }
                });
                assignedCount++;
              }
            });
            
            console.log('✅ Extracted and applied assignments from', assignedCount, 'fields (legacy XFDF)');
          } catch (error) {
            console.error('Error parsing legacy XFDF:', error);
          }
        } else if (this.signatureMode) {
          console.warn('⚠️ No field assignments provided in sign mode');
        } else {
          console.log('📝 Simulation mode: No field assignments to apply');
        }
        
        const annotations = annotationManager.getAnnotationsList();
        console.log('🔍 Total annotations:', annotations.length);
        
        // Extract unique participants from field custom data
        const participantsMap = {};
        const fields = [];
        const processedFieldNames = new Set(); // Track field names to avoid duplicates
        
        // First, check annotations (for signature widgets and free text)
        annotations.forEach((annot, index) => {
          // Get the field name first - we'll use it to check the actual field type
          let fieldName = '';
          if (annot.fieldName) {
            fieldName = annot.fieldName;
          } else if (annot.getField && typeof annot.getField === 'function') {
            const field = annot.getField();
            fieldName = field && field.name ? field.name : '';
          }
          
          // Get the actual field to check its type
          let actualField = null;
          if (fieldName) {
            actualField = fieldManager.getField(fieldName);
          }
          
          // Determine if this is truly a signature based on the field type, not just the annotation
          const isSignatureField = actualField && actualField.type === 'Sig';
          const isSignatureAnnotation = annot instanceof this.instance.Core.Annotations.SignatureWidgetAnnotation;
          
          const isFreeText = annot.Subject === 'Free Text';
          const isTextWidget = annot instanceof this.instance.Core.Annotations.TextWidgetAnnotation;
          const isRequired = annot.getCustomData('required') === 'true';
          
          // Debug logging for fields "2" and "3"
          if (fieldName === '2' || fieldName === '3') {
            console.log('Annotation for field ' + fieldName + ':', {
              subject: annot.Subject,
              isSignatureField: isSignatureField,
              isSignatureAnnotation: isSignatureAnnotation,
              actualFieldType: actualField ? actualField.type : 'no field',
              annotationType: annot.constructor.name
            });
          }
          
          // Only process Free Text annotations here
          // Skip ALL widget annotations - they'll be processed via FieldManager for proper isFilled detection
          const isWidget = annot.Subject === 'Widget' || annot.isWidget;
          
          if (isFreeText && !isWidget) {
            // Skip if we've already processed this field name (avoid duplicates from multiple widgets)
            if (fieldName && processedFieldNames.has(fieldName)) {
              return;
            }
            if (fieldName) {
              processedFieldNames.add(fieldName);
            }
            
            const participantEmail = annot.getCustomData('participantEmail') || annot.getCustomData('recipientEmail') || defaultParticipantEmail;
            const participantId = annot.getCustomData('participantId') || annot.getCustomData('recipientIndex') || participantEmail;
            const participantName = annot.getCustomData('participantName') || annot.getCustomData('recipientName') || 'Signer (Not Assigned)';
            const participantColor = annot.getCustomData('participantColor') || annot.getCustomData('recipientColor') || '#1976D2';
            
            console.log(`🔍 Field "${fieldName}": participantEmail="${participantEmail}", participantId="${participantId}"`);
            
            // Add to participants map
            if (!participantsMap[participantId]) {
              participantsMap[participantId] = {
                id: participantId,
                name: participantName,
                email: participantEmail,
                color: participantColor
              };
              console.log(`✅ Added participant: ${participantEmail} (${participantName})`);
            }
            
            // Determine field type
            const isSignature = isSignatureField || isSignatureAnnotation;
            let fieldType = 'text';
            if (isSignature) {
              fieldType = 'signature';
            } else if (isTextWidget) {
              fieldType = 'text';
            }
            
            // Add field info (for Free Text annotations only)
            fields.push({
              id: annot.Id || 'field_' + index,
              fieldType: fieldType,
              subject: annot.Subject || 'Form Field',
              required: isRequired || isSignature,
              isFilled: true, // Free text annotations with content are considered filled
              participantId: participantId,
              participantEmail: participantEmail, // Add participantEmail
              participantName: participantName,
              participantColor: participantColor,
              pageNumber: annot.PageNumber,
              contents: annot.getContents ? annot.getContents() : '',
              fieldName: fieldName,
              x: annot.X,
              y: annot.Y,
              width: annot.Width,
              height: annot.Height
            });
          }
        });
        
        // Also check PDF form fields from FieldManager
        const pdfFields = fieldManager.getFields();
        Object.keys(pdfFields).forEach((fieldKey, index) => {
          const field = pdfFields[fieldKey];
          
          // Get the actual field name from the field object
          const fieldName = field.name || fieldKey;
          
          // Check if we already added this field via annotations
          if (processedFieldNames.has(fieldName)) {
            return;
          }
          processedFieldNames.add(fieldName);
          
          // Get field widgets (visual representations)
          // Note: A single field can have multiple widgets (e.g., radio button groups)
          const widgets = field.widgets || [];
          
          // Only add ONE entry per field, using the first widget for positioning
          if (widgets.length > 0) {
            const widget = widgets[0]; // Use first widget for positioning
            
            // In Sign mode, try to get participant data from widget custom data (from XFDF)
            // Use recipientEmail if in sign mode, otherwise default to simulation email
            // UPDATED: In strict recipient mode, default to null if unassigned
            let defaultEmail = this.signatureMode ? null : 'signer@simulation.local';
            
            let participantEmail = defaultEmail;
            let participantId = defaultEmail;
            let participantName = 'Signer (Not Assigned)';
            let participantColor = '#1976D2';
            
            // Check if widget has custom data (from XFDF import in Sign mode)
            if (widget.getCustomData) {
              const customEmail = widget.getCustomData('participantEmail') || widget.getCustomData('recipientEmail');
              const customId = widget.getCustomData('participantId') || widget.getCustomData('recipientIndex');
              const customName = widget.getCustomData('participantName') || widget.getCustomData('recipientName');
              const customColor = widget.getCustomData('participantColor') || widget.getCustomData('recipientColor');
              
              if (customEmail) {
                participantEmail = customEmail;
                participantId = customId || customEmail;
                participantName = customName || customEmail;
                participantColor = customColor || '#1976D2';
                console.log(`📝 Found assignment in widget for field "${fieldName}": ${participantEmail} (${participantName})`);
              }
            }
            
            if (!participantsMap[participantId]) {
              participantsMap[participantId] = {
                id: participantId,
                name: participantName,
                email: participantEmail,
                color: participantColor
              };
            }
            
            // Determine field type more accurately
            let fieldType = 'text';
            let fieldSubject = 'Form Field';
            
            if (field.type === 'Sig') {
              fieldType = 'signature';
              fieldSubject = 'Signature';
            } else if (field.type === 'Btn') {
              // Button field - could be checkbox or radio
              if (widgets.length > 1) {
                fieldType = 'radio';
                fieldSubject = 'Radio Group';
              } else {
                fieldType = 'checkbox';
                fieldSubject = 'Checkbox';
              }
            } else if (field.type === 'Tx') {
              fieldType = 'text';
              fieldSubject = 'Text Field';
            } else if (field.type === 'Ch') {
              fieldType = 'choice';
              fieldSubject = 'Dropdown/List';
            }
            
            // Check if field is required using PDFTron API constants
            let isRequired = false;
            
            // Try to use the proper API for checking flags
            const WidgetFlags = this.instance.Core.Annotations.WidgetFlags;
            if (field.flags) {
              // Method 1: Check using 'get' method if available (standard API)
              if (typeof field.flags.get === 'function') {
                isRequired = field.flags.get(WidgetFlags.REQUIRED);
              } 
              // Method 2: Check direct property access (some versions)
              else if (field.flags.Required === true) {
                isRequired = true;
              }
              // Method 3: Check if flags is a number (bitmask)
              else if (typeof field.flags === 'number') {
                isRequired = !!(field.flags & 2);
              }
              // Method 4: Handle the object structure we saw in debug logs
              // It seems field.flags might contain the flag values themselves
              else if (typeof field.flags === 'object') {
                // If the object has a 'get' property that is a function (obfuscated/minified)
                // We might need to check specific internal properties based on the debug log
                // The debug log showed 'Jy' property holding something
                
                // Let's try to access the flags using the field.IsRequired property if available (some versions)
                if (field.isRequired && typeof field.isRequired === 'function') {
                  isRequired = field.isRequired();
                }
              }
            }
            
            // Fallback: Signatures are always required in our context
            if (fieldType === 'signature') {
              isRequired = true;
            }
            
            // Try to find a better display name (Tooltip/Alternate Name often holds the readable label)
            let displayName = fieldName;
            if (field.tooltipName) {
              displayName = field.tooltipName;
            } else if (field.name && field.name !== fieldName) {
              displayName = field.name;
            }
            
            // If the name is just a number, try to find a label from a nearby text widget or annotation
            // (This is a common issue in auto-generated PDFs)
            
            // Check if this field is already filled (especially important for signature fields)
            // Note: For form fields, we check the field value and annotation state
            let isFilled = false;
            
            // For signature fields, check if there's a signature annotation present
            if (field.type === 'Sig') {
              // Define widget rectangle for intersection checks
              const widgetRect = {
                x1: widget.X,
                y1: widget.Y,
                x2: widget.X + widget.Width,
                y2: widget.Y + widget.Height
              };
              
              // Method 1: Check field value first
              console.log(`  🔍 Signature field "${fieldName}" properties:`, {
                fieldValue: field.value,
                fieldFlags: field.flags,
                isReadOnly: field.flags && field.flags.ReadOnly,
                isLocked: field.flags && field.flags.Locked,
                widgets: field.widgets,
                widgetCount: field.widgets ? field.widgets.length : 0
              });
              
              if (field.value && field.value !== '') {
                isFilled = true;
                console.log(`✅ Signature field "${fieldName}" is filled (has field value):`, field.value);
              }
              
              // Check if field is locked (indicates it's been signed and finalized)
              if (!isFilled && field.flags && (field.flags.ReadOnly || field.flags.Locked)) {
                isFilled = true;
                console.log(`✅ Signature field "${fieldName}" is filled (field is locked/read-only)`);
              }
              
              // Method 2: Check if the widget itself has signature image data embedded
              if (!isFilled && widget) {
                const appearancesObj = widget.appearances || {};
                const appearanceKeys = Object.keys(appearancesObj);
                
                console.log(`  🔍 Checking widget properties for "${fieldName}":`, {
                  hasImageData: widget.ImageData && widget.ImageData.length > 0,
                  ImageDataLength: widget.ImageData ? widget.ImageData.length : 0,
                  hasPaths: widget.getPaths ? widget.getPaths().length > 0 : false,
                  pathsLength: widget.getPaths ? widget.getPaths().length : 0,
                  hasAppearances: widget.appearances ? true : false,
                  appearanceKeys: appearanceKeys,
                  hasNormalAppearance: widget.appearances && widget.appearances.Normal ? true : false,
                  normalAppearanceType: widget.appearances && widget.appearances.Normal ? typeof widget.appearances.Normal : 'none',
                  appearance: widget.appearance ? 'HAS APPEARANCE' : 'NO APPEARANCE',
                  ImageData: widget.ImageData ? 'HAS IMAGE DATA' : 'NO IMAGE DATA'
                });
                
                const hasImageData = widget.ImageData && widget.ImageData.length > 0;
                const hasPaths = widget.getPaths && widget.getPaths().length > 0;
                
                // IMPORTANT: Don't check appearance streams alone - they can contain "Sign here" placeholders
                // Only mark as filled if there's actual signature data (ImageData or paths)
                if (hasImageData || hasPaths) {
                  isFilled = true;
                  const reason = hasImageData ? 'ImageData' : 'paths';
                  console.log(`✅ Signature field "${fieldName}" is filled (widget has ${reason})`);
                } else {
                  console.log(`⚠️ Signature field "${fieldName}" appears EMPTY (no ImageData or paths)`);
                }
              }
              
              if (!isFilled) {
                // Check if there's a signature annotation overlapping this widget
                const annotationManager = this.instance.Core.annotationManager;
                const allAnnots = annotationManager.getAnnotationsList();
                
                // Find annotations on the same page first
                const samePageAnnots = allAnnots.filter(a => a.PageNumber === widget.PageNumber);
                console.log(`🔍 Signature field "${fieldName}": Checking ${samePageAnnots.length} annotations on page ${widget.PageNumber}`);
                
                // Log ALL annotations on this page to see what we're missing
                console.log(`  📋 ALL annotations on page ${widget.PageNumber}:`);
                samePageAnnots.forEach((a, idx) => {
                  const rect = a.getRect();
                  console.log(`    [${idx}] ${a.Subject || a.elementName}:`, {
                    Subject: a.Subject,
                    elementName: a.elementName,
                    rect: `(${Math.round(rect.x1)}, ${Math.round(rect.y1)}) -> (${Math.round(rect.x2)}, ${Math.round(rect.y2)})`,
                    hasImageData: a.ImageData && a.ImageData.length > 0,
                    hasImageSrc: a.getImageData && a.getImageData() !== '',
                    hasContents: a.getContents && a.getContents() !== '',
                    hasPaths: a.getPaths && a.getPaths().length > 0,
                    hasAppearances: a.appearances ? Object.keys(a.appearances) : []
                  });
                });
                
                // Check each annotation for signature types
                const signatureAnnots = samePageAnnots.filter(annot => {
                  const isSignatureType = annot.Subject === 'Signature' || 
                                         annot.Subject === 'Stamp' || 
                                         annot.Subject === 'Ink' ||
                                         annot.Subject === 'Free Text' ||
                                         (annot.ImageData && annot.ImageData.length > 0) ||
                                         (annot.getPaths && annot.getPaths().length > 0);
                  
                  if (isSignatureType) {
                    const annotRect = annot.getRect();
                    console.log(`  📝 Found potential signature (${annot.Subject || annot.elementName}) at:`, {
                      x1: annotRect.x1,
                      y1: annotRect.y1,
                      x2: annotRect.x2,
                      y2: annotRect.y2,
                      hasImageData: annot.ImageData && annot.ImageData.length > 0,
                      hasContent: annot.getContents && annot.getContents() !== '',
                      hasPaths: annot.getPaths && annot.getPaths().length > 0
                    });
                  }
                  
                  return isSignatureType;
                });
                
                console.log(`  📊 Found ${signatureAnnots.length} potential signature annotations`);
                console.log(`  📍 Widget rect:`, widgetRect);
                
                const hasSignatureAnnot = signatureAnnots.some(annot => {
                  // Check if it overlaps with the widget
                  const annotRect = annot.getRect();
                  const intersects = !(annotRect.x1 > widgetRect.x2 || 
                                       annotRect.x2 < widgetRect.x1 || 
                                       annotRect.y1 > widgetRect.y2 || 
                                       annotRect.y2 < widgetRect.y1);
                  
                  if (intersects) {
                    console.log(`  ✅ Found OVERLAPPING ${annot.Subject} annotation!`);
                  }
                  
                  return intersects;
                });
                
                if (hasSignatureAnnot) {
                  isFilled = true;
                  console.log(`✅ Signature field "${fieldName}" is filled (has overlapping signature annotation)`);
                } else {
                  // Log when signature field appears empty
                  console.log(`⚠️ Signature field "${fieldName}" appears empty:`, {
                    fieldValue: field.value,
                    widgetRect: widgetRect,
                    totalAnnotations: allAnnots.length,
                    signatureAnnotsOnPage: signatureAnnots.length
                  });
                }
              }
            } else {
              // For non-signature fields, check the field value
              const value = field.value;
              
              // Special handling for checkboxes and radio buttons (Btn type)
              if (field.type === 'Btn') {
                // For buttons, 'Off' means unchecked/unselected, anything else means filled
                if (value !== null && value !== undefined && value !== '' && value !== 'Off') {
                  isFilled = true;
                  console.log(`✅ Button field "${fieldName}" is filled with value:`, value);
                } else {
                  console.log(`⚠️ Button field "${fieldName}" appears empty, value:`, value);
                }
              } else {
                // For other fields (text, choice), empty string means unfilled
                if (value !== null && value !== undefined && value !== '') {
                  const strValue = String(value).trim();
                  if (strValue !== '') {
                    isFilled = true;
                    console.log(`✅ Field "${fieldName}" is filled with value:`, value);
                  }
                }
              }
            }
            
            fields.push({
              id: 'formfield_' + fieldName,
              fieldType: fieldType,
              subject: fieldSubject,
              required: isRequired,
              isFilled: isFilled, // Add filled status
              participantId: participantId,
              participantEmail: participantEmail, // Add participantEmail to field data
              participantName: participantName,
              participantColor: participantColor,
              pageNumber: widget.PageNumber || 1,
              contents: field.value || '',
              fieldName: fieldName,
              displayName: displayName, // Add the display name
              x: widget.X || 0,
              y: widget.Y || 0,
              width: widget.Width || 0,
              height: widget.Height || 0,
              widgetCount: widgets.length, // Track how many widgets this field has
              pdfFieldType: field.type, // Store original PDF field type
              flags: field.flags // Store flags for debugging
            });
          }
        });
        
        // Convert participants map to array
        const participants = Object.values(participantsMap);
        
        // Count field types for debugging
        const signatureFields = fields.filter(f => f.fieldType === 'signature');
        const filledFields = fields.filter(f => f.isFilled);
        const fieldTypeCounts = {
          signature: signatureFields.length,
          text: fields.filter(f => f.fieldType === 'text').length,
          filled: filledFields.length
        };
        
        console.log('Extracted field information:', {
          total: fields.length,
          signatures: fieldTypeCounts.signature,
          textFields: fieldTypeCounts.text,
          filled: fieldTypeCounts.filled,
          participants: participants.length
        });
        
        // Log filled fields for debugging
        if (filledFields.length > 0) {
          console.log('✅ Already-filled fields detected:', filledFields.map(f => ({
            fieldName: f.fieldName,
            fieldType: f.fieldType,
            participantEmail: f.participantEmail,
            fieldValue: f.contents
          })));
        } else {
          console.log('⚠️ No pre-filled fields detected');
        }
        
        // Log signature field details to debug duplicates
        console.log('Signature fields detected:', signatureFields.map(f => ({
          id: f.id,
          fieldName: f.fieldName,
          subject: f.subject,
          page: f.pageNumber,
          fieldType: f.fieldType,
          pdfFieldType: f.pdfFieldType,
          isFilled: f.isFilled
        })));
        
        // Log all fields for debugging
        console.log('All fields detected:', fields.map(f => ({
          fieldName: f.fieldName,
          fieldType: f.fieldType,
          subject: f.subject,
          required: f.required,
          pdfFieldType: f.pdfFieldType,
          flags: f.flags
        })));
        
        // Full JSON dump of extracted fields (safe version)
        console.log('=== EXTRACTED FIELDS JSON ===');
        const safeFields = fields.map(f => {
          const safeFlagsValue = typeof f.flags === 'object' ? 
            { Required: f.flags.Required, ReadOnly: f.flags.ReadOnly } : 
            f.flags;
          
          return {
            id: f.id,
            fieldName: f.fieldName,
            fieldType: f.fieldType,
            pdfFieldType: f.pdfFieldType,
            subject: f.subject,
            required: f.required,
            participantEmail: f.participantEmail,
            pageNumber: f.pageNumber,
            contents: f.contents,
            x: f.x,
            y: f.y,
            widgetCount: f.widgetCount,
            flags: safeFlagsValue
          };
        });
        console.log(JSON.stringify(safeFields, null, 2));
        
        // Summary logging
        console.log('🔍 ===== getFieldsInformation SUMMARY =====');
        console.log('🔍 Mode:', this.signatureMode ? 'SIGN' : 'SIMULATION');
        console.log('🔍 Total fields extracted:', fields.length);
        console.log('🔍 Total participants found:', participants.length);
        
        // Group fields by participant
        const fieldsByParticipant = {};
        fields.forEach(f => {
          const email = f.participantEmail || 'unassigned';
          if (!fieldsByParticipant[email]) {
            fieldsByParticipant[email] = [];
          }
          fieldsByParticipant[email].push(f.fieldName);
        });
        
        console.log('🔍 Fields by participant:');
        Object.keys(fieldsByParticipant).forEach(email => {
          console.log(`   ${email}: ${fieldsByParticipant[email].length} fields -`, fieldsByParticipant[email]);
        });
        
        console.log('🔍 Participants:', participants.map(p => `${p.name} (${p.email})`));
        console.log('🔍 ===== getFieldsInformation END =====');
        
        return {
          fields: fields,
          participants: participants
        };
      } catch (error) {
        console.error('Error extracting field information:', error);
        return null;
      }
    },
    async scanAndSetupFormFields(instance) {
      // Scan the PDF for existing form fields and annotations for simulation mode
      console.log('╔════════════════════════════════════════════════════════════╗');
      console.log('║         PDF SIMULATION MODE - FIELD SCAN DEBUG             ║');
      console.log('╚════════════════════════════════════════════════════════════╝');
      
      const annotationManager = instance.Core.annotationManager;
      const fieldManager = annotationManager.getFieldManager();
      
      // Wait a bit for all fields to be loaded
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get all form fields from the document
      const fields = fieldManager.getFields();
      console.log('Scanned form fields:', fields);
      
      // Log each field with full details
      console.log('=== DETAILED FIELD DATA ===');
      Object.keys(fields).forEach(fieldName => {
        const field = fields[fieldName];
        
        // Safely extract flags for debugging
        let debugFlags = {};
        try {
          const WidgetFlags = this.instance.Core.Annotations.WidgetFlags;
          debugFlags = {
            isRequired: field.flags && typeof field.flags.get === 'function' ? field.flags.get(WidgetFlags.REQUIRED) : 'n/a',
            isReadOnly: field.flags && typeof field.flags.get === 'function' ? field.flags.get(WidgetFlags.READ_ONLY) : 'n/a',
            rawFlagsType: typeof field.flags,
            rawFlagsKeys: field.flags ? Object.keys(field.flags) : []
          };
        } catch (e) {
          debugFlags = { error: e.message };
        }

        // Safe extraction of field properties
        const safeField = {
          name: field.name,
          type: field.type,
          value: field.value,
          defaultValue: field.defaultValue,
          exportValue: field.exportValue,
          flags: debugFlags,
          widgetCount: field.widgets ? field.widgets.length : 0,
          options: field.options,
          maxLen: field.maxLen,
          tooltipName: field.tooltipName
        };
        
        console.log('Field: ' + fieldName, safeField);
      });
      
      // Get all annotations (including signature widgets)
      const annotations = annotationManager.getAnnotationsList();
      console.log('Scanned annotations:', annotations.length);
      
      // Log each annotation with full details
      console.log('=== DETAILED ANNOTATION DATA ===');
      annotations.forEach((annot, index) => {
        // Safe extraction of annotation properties
        const safeAnnot = {
          id: annot.Id,
          subject: annot.Subject,
          type: annot.constructor.name,
          fieldName: annot.fieldName,
          pageNumber: annot.PageNumber,
          x: annot.X,
          y: annot.Y,
          width: annot.Width,
          height: annot.Height,
          contents: annot.getContents ? annot.getContents() : '',
          customData: {
            required: annot.getCustomData('required'),
            participantId: annot.getCustomData('participantId'),
            participantEmail: annot.getCustomData('participantEmail'),
            recipientIndex: annot.getCustomData('recipientIndex')
          },
          isReadOnly: annot.ReadOnly,
          isHidden: annot.Hidden
        };
        
        // Get field info if this is a widget
        if (annot.fieldName) {
          const linkedField = fieldManager.getField(annot.fieldName);
          if (linkedField) {
            safeAnnot.linkedFieldType = linkedField.type;
            safeAnnot.linkedFieldFlags = linkedField.flags;
          }
        }
        
        console.log('Annotation ' + index + ':', safeAnnot);
      });
      
      // Filter for interactive elements (form fields, signatures, etc.)
      const formAnnotations = annotations.filter(annot => {
        return annot.Subject === 'Free Text' || 
               annot.Subject === 'Signature' ||
               annot instanceof instance.Core.Annotations.TextWidgetAnnotation ||
               annot instanceof instance.Core.Annotations.SignatureWidgetAnnotation ||
               (annot.getFormFieldPlaceholderType && annot.getFormFieldPlaceholderType());
      });
      
      console.log('Found interactive form annotations:', formAnnotations.length);
      
      console.log('╔════════════════════════════════════════════════════════════╗');
      console.log('║                    SCAN SUMMARY                            ║');
      console.log('╠════════════════════════════════════════════════════════════╣');
      console.log('║ Total PDF Fields:        ' + Object.keys(fields).length);
      console.log('║ Total Annotations:       ' + annotations.length);
      console.log('║ Interactive Annotations: ' + formAnnotations.length);
      console.log('╚════════════════════════════════════════════════════════════╝');
      
      // If we found form fields, set up guided signing
      if (formAnnotations.length > 0 || Object.keys(fields).length > 0) {
        this.setupGuidedSigning(instance);
      } else {
        // No form fields found - emit ready event anyway with 0 fields
        this.$emit('guidedSigningReady', {
          totalFields: 0,
          navigateNext: () => {},
          navigatePrevious: () => {}
        });
      }
    },
    setActiveParticipant(participantId) {
      this.activeParticipantEmail = participantId;
      // Filter only signature fields by participant in simulation mode
      // All non-signature fields remain visible and editable
      if (!this.simulationMode || !this.instance || !this.instance.Core) {
        return;
      }
      
      try {
        const annotationManager = this.instance.Core.annotationManager;
        const annotations = annotationManager.getAnnotationsList();
        const documentViewer = this.instance.Core.documentViewer;
        const fieldManager = documentViewer.getAnnotationManager().getFieldManager();
        
        annotations.forEach(annot => {
          // Check both participantId and participantEmail for matching
          const annotParticipantId = annot.getCustomData('participantId') || annot.getCustomData('recipientIndex');
          const annotParticipantEmail = annot.getCustomData('participantEmail') || annot.getCustomData('recipientEmail');
          
          // Only filter SIGNATURE fields by participant
          // Check if this is a signature field (either annotation Subject or associated field type)
          let isSignatureField = false;
          
          if (annot.Subject === 'Signature') {
            isSignatureField = true;
          } else if (annot.fieldName) {
            // Check if the associated form field is a signature field
            const field = fieldManager.getField(annot.fieldName);
            if (field && field.type === 'Sig') {
              isSignatureField = true;
            }
          }
          
          if (isSignatureField) {
            // Filter signature fields by participant
            const matches = participantId === null || 
                          participantId === 'all' || 
                          annotParticipantId === participantId || 
                          annotParticipantEmail === participantId;
            
            if (matches) {
              annot.Hidden = false;
              annot.ReadOnly = false;
            } else {
              annot.Hidden = true;
              annot.ReadOnly = true;
            }
          } else {
            // All non-signature fields remain visible and editable
            annot.Hidden = false;
            annot.ReadOnly = false;
          }
        });
        
        annotationManager.drawAnnotationsFromList(annotations);
        
        // Apply field styling after filtering
        this.scheduleFieldStyling(100);
        this.updateButtonState();
        // Hide arrow when switching participants until they click Next
        this.showArrow = false; 
        this.nextButtonText = "Start";
        this.$emit('updateNextButtonText', this.nextButtonText);
        
      } catch (error) {
        console.error('Error setting active participant:', error);
      }
    },
    async updateFieldParticipant(fieldId, participant) {
      // Update the participant assignment for a specific field
      if (!this.instance || !this.instance.Core) {
        console.warn('PDF viewer not ready');
        return;
      }
      
      try {
        const annotationManager = this.instance.Core.annotationManager;
        const annotations = annotationManager.getAnnotationsList();
        
        // Find the annotation matching this field ID
        const annot = annotations.find(a => {
          const annotId = 'formfield_' + a.fieldName || a.getCustomData('fieldId');
          return annotId === fieldId || a.Id === fieldId;
        });
        
        if (annot) {
          // Update custom data with new participant info
          annot.setCustomData('participantId', participant.email);
          annot.setCustomData('participantEmail', participant.email);
          annot.setCustomData('participantName', participant.name);
          annot.setCustomData('participantColor', participant.color);
          
          // Update the annotation appearance/color if needed
          annot.StrokeColor = new this.instance.Core.Annotations.Color(participant.color);
          
          annotationManager.redrawAnnotation(annot);
        } else {
          console.warn('Could not find annotation for field:', fieldId);
        }
        
      } catch (error) {
        console.error('Error updating field participant:', error);
      }
    },
    async updateAllFieldParticipants(signatureFields) {
      // Batch update all signature field annotations with participant data
      if (!this.instance || !this.instance.Core) {
        console.warn('PDF viewer not ready');
        return;
      }
      
      try {
        const annotationManager = this.instance.Core.annotationManager;
        const annotations = annotationManager.getAnnotationsList();
        const documentViewer = this.instance.Core.documentViewer;
        const fieldManager = documentViewer.getAnnotationManager().getFieldManager();
        
        let updatedCount = 0;
        
        // For each signature field, find its annotation(s) and update participant data
        signatureFields.forEach(field => {
          annotations.forEach(annot => {
            // Check if this annotation belongs to this field by matching fieldName
            let matches = false;
            
            // Try matching by field name (the annotation's fieldName should match the field's fieldName)
            if (annot.fieldName && field.fieldName && annot.fieldName === field.fieldName) {
              matches = true;
            }
            
            // Also check if it's a signature widget on the same page
            if (!matches) {
              const annotField = fieldManager.getField(annot.fieldName);
              if (annotField && annotField.type === 'Sig') {
                // Check page number matches
                if (annot.PageNumber === field.pageNumber && annot.fieldName === field.fieldName) {
                  matches = true;
                }
              }
            }
            
            if (matches && field.participantEmail) {
              // Update custom data with participant info
              annot.setCustomData('participantId', field.participantEmail);
              annot.setCustomData('participantEmail', field.participantEmail);
              annot.setCustomData('participantName', field.participantName || field.participantEmail);
              annot.setCustomData('participantColor', field.participantColor || '#1976D2');
              
              // Update appearance
              if (field.participantColor) {
                annot.StrokeColor = new this.instance.Core.Annotations.Color(field.participantColor);
              }
              
              updatedCount++;
            }
          });
        });
        
        // Redraw all annotations
        annotationManager.drawAnnotationsFromList(annotations);
        
      } catch (error) {
        console.error('Error updating all field participants:', error);
      }
    },
    setRequiredFields(fieldsList) {
      // Method to be called from simulation controller with list of all fields and their required status
      if (!this.instance || !this.instance.Core) {
        console.warn('PDF viewer not ready for setting required fields');
        return;
      }
      
      try {
        const annotationManager = this.instance.Core.annotationManager;
        const annotations = annotationManager.getAnnotationsList();
        const newRequiredFields = [];
        
        // Create a map for faster lookup
        const fieldInfoMap = new Map();
        fieldsList.forEach(f => {
            if (f.fieldName) fieldInfoMap.set(f.fieldName, f);
        });
        
        // Iterate over ALL annotations to update their status
        annotations.forEach(annot => {
            // Skip non-widget annotations
            if (!annot.fieldName && annot.Subject !== 'Free Text' && annot.Subject !== 'Signature') {
                return;
            }
            
            // Check if this annotation matches a field in our provided list
            const fieldName = annot.fieldName;
            const fieldInfo = fieldInfoMap.get(fieldName);
            
            if (fieldInfo) {
                // MATCH FOUND: Update with provided info
                annot.setCustomData('required', fieldInfo.required ? 'true' : 'false');
                annot.setCustomData('fieldType', fieldInfo.fieldType || 'text');

                // Set participant data
                if (fieldInfo.participantEmail) {
                    annot.setCustomData('participantEmail', fieldInfo.participantEmail);
                    annot.setCustomData('participantId', fieldInfo.participantId || fieldInfo.participantEmail);
                    annot.setCustomData('participantName', fieldInfo.participantName);
                    annot.setCustomData('participantColor', fieldInfo.participantColor);
                }
                
                // If it's required, add to our local list for navigation
                if (fieldInfo.required || fieldInfo.fieldType === 'signature') {
                    // Avoid duplicates
                    if (!newRequiredFields.includes(annot)) {
                        newRequiredFields.push(annot);
                    }
                }
            } else {
                // NO MATCH: This field is not in the provided list (e.g. assigned to another user)
                // Explicitly mark as NOT required so applyFieldStyling knows to ignore it
                annot.setCustomData('required', 'false');
            }
        });
        
        // Update the main requiredFields list
        if (newRequiredFields.length > 0) {
          console.log('Updating requiredFields in WebViewer from controller data:', newRequiredFields.length);
          
          // Debug: Log field positions before sorting
          console.log('📍 Field positions BEFORE sort:');
          newRequiredFields.forEach(f => {
            const rect = f.Rect || f.rect;
            console.log(`  ${f.fieldName}: Page=${f.PageNumber}, Rect.y1=${rect ? rect.y1 : 'N/A'}, Rect.y2=${rect ? rect.y2 : 'N/A'}, annot.Y=${f.Y}, annot.X=${f.X}`);
          });
          
          // Sort fields by page and position (Top-Left to Bottom-Right)
          // CRITICAL: In PDF coordinates, Y=0 is at BOTTOM and increases UPWARD
          // BUT when we convert to screen coordinates, we flip it: smaller Y = higher on page
          // So for top-down navigation, we need ASCENDING Y (smaller Y = top of page)
          newRequiredFields.sort((a, b) => {
            if (a.PageNumber !== b.PageNumber) return a.PageNumber - b.PageNumber;
            
            // Use Rect.y1 (bottom edge in PDF coords, which is TOP in visual terms after flip)
            const aRect = a.Rect || a.rect;
            const bRect = b.Rect || b.rect;
            const aY = aRect ? aRect.y1 : a.Y;
            const bY = bRect ? bRect.y1 : b.Y;
            const aX = aRect ? aRect.x1 : a.X;
            const bX = bRect ? bRect.x1 : b.X;
            
            // Sort by ASCENDING Y (smaller Y value = visually higher on page)
            if (Math.abs(aY - bY) > 10) return aY - bY;
            
            return aX - bX; // Left-to-right (ascending X)
          });

          // Filter out duplicates by fieldName, keeping the first one (which is the top-most/left-most due to sort)
          // This ensures that for radio groups (which have multiple widgets with the same name),
          // we only navigate to the first widget, not every single option.
          const uniqueRequiredFields = [];
          const seenFieldNames = new Set();
          
          newRequiredFields.forEach(annot => {
              const name = annot.fieldName;
              // If field has a name, ensure uniqueness
              if (name) {
                  if (!seenFieldNames.has(name)) {
                      seenFieldNames.add(name);
                      uniqueRequiredFields.push(annot);
                  }
              } else {
                  // If no name (rare for required fields), keep it
                  uniqueRequiredFields.push(annot);
              }
          });
          
          // Debug: Log field positions after sorting and deduplication
          console.log('📍 Field positions AFTER sort and deduplication (should be top-down):');
          uniqueRequiredFields.forEach((f, idx) => {
            const rect = f.Rect || f.rect;
            console.log(`  [${idx}] ${f.fieldName}: Page=${f.PageNumber}, Rect.y2=${rect ? rect.y2 : 'N/A'}, Rect.y1=${rect ? rect.y1 : 'N/A'}`);
          });
          
          this.requiredFields = uniqueRequiredFields;
          
          // Reset navigation state when fields are updated
          this.currentFieldIndex = -1;
          this.nextButtonText = "Start";
          this.showArrow = false;
          this.$emit('updateNextButtonText', this.nextButtonText);
          
          // Update UI state
          this.updateButtonState();
          
          // Ensure UI is visible
          if (this.simulationMode) {
            this.guidedSigningReady = true;
          }
        }
        
        // Apply styling to reflect the new required status
        // This ensures fields that were removed from requiredFields (unassigned) are visually updated
        this.scheduleFieldStyling(300);
        
      } catch (error) {
        console.error('Error setting required fields:', error);
      }
    },
    async applyFieldAssignments() {
      if (!this.signatureMode || !this.fieldAssignments) {
        console.log('📝 applyFieldAssignments: Skipping (not sign mode or no assignments)');
        return;
      }
      
      console.log('📝 applyFieldAssignments: Applying ' + Object.keys(this.fieldAssignments).length + ' assignments to annotations...');
      
      try {
        const annotationManager = this.instance.Core.annotationManager;
        const fieldManager = annotationManager.getFieldManager();
        const annotations = annotationManager.getAnnotationsList();
        let assignedCount = 0;
        
        // Create Set of assigned field names for fast lookup
        const assignedFieldNames = new Set(Object.keys(this.fieldAssignments));
        const annotationsToUpdate = [];
        
        // Iterate ALL widget annotations to apply assignments AND handle unassigned fields
        annotations.forEach(annot => {
          // Skip non-widgets
          if (annot.Subject !== 'Widget' || !annot.fieldName) {
              return;
          }
          
          const fieldName = annot.fieldName;
          let updated = false;
          
          if (assignedFieldNames.has(fieldName)) {
              // Case 1: Field IS assigned to a recipient
              const assignment = this.fieldAssignments[fieldName];
              
              // Apply participant metadata directly
              if (assignment.participantEmail) {
                annot.setCustomData('participantEmail', assignment.participantEmail);
                updated = true;
              }
              if (assignment.participantId) {
                annot.setCustomData('participantId', assignment.participantId);
                updated = true;
              }
              if (assignment.participantName) {
                annot.setCustomData('participantName', assignment.participantName);
                updated = true;
              }
              if (assignment.participantColor) {
                annot.setCustomData('participantColor', assignment.participantColor);
                updated = true;
              }
              if (assignment.isOrganization !== undefined) {
                annot.setCustomData('isOrganization', String(assignment.isOrganization));
                updated = true;
              }
              if (assignment.isRequired !== undefined) {
                annot.setCustomData('required', String(assignment.isRequired));
                updated = true;
              }
              
              assignedCount++;
          } else {
              // Case 2: Field is NOT assigned to any recipient
              // In strict recipient mode, unassigned signatures should NOT be required
              const field = fieldManager.getField(fieldName);
              let isSignature = (field && field.type === 'Sig');
              
              // Fallback check for signature annotation subject if field manager lookup fails
              if (!isSignature && annot.Subject === 'Signature') {
                  isSignature = true;
              }
              
              if (isSignature) {
                  // Explicitly mark unassigned signature as NOT required
                  annot.setCustomData('required', 'false');
                  annot.setCustomData('participantEmail', '');
                  annot.setCustomData('participantId', '');
                  annot.setCustomData('participantName', 'Not Required');
                  annot.setCustomData('participantColor', '#9E9E9E');
                  updated = true;
              }
          }
          
          if (updated) {
            annotationsToUpdate.push(annot);
          }
        });
        
        // Redraw annotations if needed
        if (annotationsToUpdate.length > 0) {
            annotationManager.drawAnnotationsFromList(annotationsToUpdate);
        }
        
        console.log('✅ Applied assignments to', assignedCount, 'fields (updated annotations)');
        
        // Re-trigger field styling to visually reflect changes
        this.scheduleFieldStyling(100);
        
      } catch (error) {
        console.error('Error applying field assignments:', error);
      }
    },
    async validatePdfSignatures() {
      // Validate digital signatures in the PDF
      if (!this.instance || !this.instance.Core) {
        console.warn('PDF viewer not ready for signature validation');
        return null;
      }
      
      try {
        const Core = this.instance.Core;
        const doc = Core.documentViewer.getDocument();
        
        // Check if document has digital signatures API
        if (!doc.getDigitalSignatures) {
          console.warn('Digital signatures API not available in this version of PDFTron');
          return {
            supported: false,
            message: 'Digital signature validation is not supported in this browser or PDF viewer version'
          };
        }
        
        const digitalSignatures = await doc.getDigitalSignatures();
        
        if (!digitalSignatures || digitalSignatures.length === 0) {
          return {
            supported: true,
            hasSignatures: false,
            signatures: [],
            overallStatus: 'none',
            message: 'This document has not been digitally signed'
          };
        }
        
        // Validate each signature
        const signatureResults = [];
        let hasValid = false;
        let hasInvalid = false;
        let hasUntrusted = false;
        
        for (const signature of digitalSignatures) {
          try {
            const isValid = await signature.verify();
            const isTrusted = signature.isTrusted ? signature.isTrusted() : false;
            const hasChanged = signature.hasChanged ? signature.hasChanged() : false;
            
            const result = {
              fieldName: signature.name || 'Unknown',
              isValid: isValid,
              isTrusted: isTrusted,
              hasChanged: hasChanged,
              signerName: signature.getSignerName ? signature.getSignerName() : 'Unknown',
              signDate: signature.getSigningDate ? signature.getSigningDate() : null
            };
            
            signatureResults.push(result);
            
            if (isValid && isTrusted && !hasChanged) {
              hasValid = true;
            } else if (!isValid || hasChanged) {
              hasInvalid = true;
            } else if (!isTrusted) {
              hasUntrusted = true;
            }
          } catch (sigError) {
            console.error('Error validating signature:', sigError);
            signatureResults.push({
              fieldName: signature.name || 'Unknown',
              error: true,
              message: 'Failed to validate signature'
            });
            hasInvalid = true;
          }
        }
        
        // Determine overall status
        let overallStatus = 'valid';
        if (hasInvalid) {
          overallStatus = 'invalid';
        } else if (hasUntrusted) {
          overallStatus = 'untrusted';
        }
        
        return {
          supported: true,
          hasSignatures: true,
          signatures: signatureResults,
          overallStatus: overallStatus,
          count: signatureResults.length
        };
      } catch (error) {
        console.error('Error validating PDF signatures:', error);
        return {
          supported: true,
          error: true,
          message: 'An error occurred while validating signatures: ' + error.message
        };
      }
    }
  }
});
</script>

<style scoped>
.viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.viewer-element {
  width: 100%;
  height: 100%;
  flex: 1;
}

.nav-overlay {
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 9999;
}

.nav-button-container {
  pointer-events: auto;
}

.nav-button {
  background-color: #ffc107;
  color: #000;
  border: none;
  padding: 12px 32px;
  border-radius: 24px;
  font-weight: bold;
  font-size: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  cursor: pointer;
  transition: transform 0.2s, background-color 0.2s;
  outline: none;
}

.nav-button:hover {
  transform: translateY(-2px);
  background-color: #ffca28;
}

.nav-button:active {
  transform: translateY(0);
}

.nav-button.finish-button {
  background-color: #4caf50;
  color: white;
}

.arrow-indicator {
  position: absolute; /* Absolute positioning relative to viewer-container */
  pointer-events: none;
  z-index: 2147483647; /* Max Z-Index */
  /* Removed transition: top/left to eliminate lag during scroll */
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
  animation: bounce 1.5s infinite;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.arrow-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.field-label {
  background-color: #ffc107;
  color: #000;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  margin-top: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

@keyframes bounce {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(-10px); }
}
</style>
